import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Copy, ArrowLeft, ExternalLink, CheckCircle2, AlertTriangle,
  DollarSign, Building2, Calendar, Hash, Plus, Send, Trash2, Banknote,
} from "lucide-react";
import ArReceiptModal from "./ArReceiptModal";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Pill, Button, Field, Input, Select, Spinner, EmptyState, KPI,
} from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";

// =============================================================================
// Client invoice worksheet
// =============================================================================
// We don't push invoices to Xero from here yet — that's Phase 2 (Xero API).
// Instead this screen presents all the info needed to fill Xero's "New
// invoice" form in the exact same layout, with copy-to-clipboard on each
// field. James clicks one button per Xero field, paste, done.
// =============================================================================

// Xero short code — pulled from env so we can override per environment.
// Add VITE_XERO_SHORTCODE=!QHgP0 to Cloudflare Pages env vars to make this
// configurable without redeploying.
const XERO_SHORTCODE =
  (import.meta as any).env?.VITE_XERO_SHORTCODE ?? "!QHgP0";

// Documented Xero deep-link pattern. Lands directly on the new invoice
// editor for the correct org.
const XERO_NEW_INVOICE_URL =
  `https://go.xero.com/organisationlogin/default.aspx` +
  `?shortcode=${XERO_SHORTCODE}` +
  `&redirecturl=${encodeURIComponent(
    "/AccountsReceivable/Edit.aspx?Type=AR&InvoiceID=00000000-0000-0000-0000-000000000000"
  )}`;

// T-504b — THE reference builder. There used to be two: this screen composed a
// string for copy-and-paste, and the Worker composed a different one from the
// stored columns. The screen said "HAR-26-1063 — COONABARABRAN / Q-26-0009 /
// 80% Progress Claim"; Xero would have received "HARDLINE-26-0004 /
// INV-26-00019". Now the worksheet composes it, stores it on the invoice, and
// the Worker sends that string verbatim.
//
// Order is what a person scanning a Xero invoice list needs, matching the
// invoices already in there ("POHLG26-01-128 / Q-26-0001, VAR-26-0001"):
// their PO first, then the job, then what was claimed, then our key.
export function defaultLineDescription(quoteNumber: string, quoteTitle: string, claimPct: number): string {
  const base = `${quoteNumber} — ${quoteTitle}`;
  return claimPct >= 100 ? base : `${base} — ${+claimPct.toFixed(2)}% claim`;
}

// 1 Nov 2026 — James, off INV-0051 in Xero ("Shroud Removal / INV-26-00038"):
// "I just want a single invoice number, don't care which one it is, and the
// Ref should be the Client PO or STOW and maybe the quote / site ref but
// nothing crazy." So: Xero's invoice number IS ours (the worker now sends it),
// and the Reference is the client's PO / STOW, the quote number(s) and the
// site — no claim milestone, never our own number. Editable on the worksheet
// before Push. Nothing already in Xero is touched.
export function buildXeroReference(parts: {
  clientPo?: string | null;       // the client's PO / STOW number
  jobName?: string | null;        // site or project name — the client's own job
  quoteNumbers?: string[];
  claimReference?: string | null; // kept for callers; no longer printed
  invoiceNumber?: string | null;  // kept for callers; no longer printed
}): string {
  const bits = [
    parts.clientPo,
    (parts.quoteNumbers ?? []).filter(Boolean).join(", ") || null,
    parts.jobName,
  ].map(v => String(v ?? "").trim()).filter(Boolean);
  return Array.from(new Set(bits)).join(" / ").slice(0, 255);
}

// T-504 — once an invoice has been pushed we can link at the real document
// instead of a blank form.
function xeroArInvoiceUrl(xeroInvoiceId: string): string {
  return `https://go.xero.com/organisationlogin/default.aspx?shortcode=${XERO_SHORTCODE}` +
    `&redirecturl=${encodeURIComponent(`/AccountsReceivable/View.aspx?InvoiceID=${xeroInvoiceId}`)}`;
}

// -----------------------------------------------------------------------------
// New invoice worksheet — launched from project detail
// -----------------------------------------------------------------------------
export function InvoiceWorksheetScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // 28 Oct 2026 — James: "make these links to the invoice so we can check them while here when trying to audit things"
  const [openPast, setOpenPast] = useState<string | null>(null);

  // T-229 — Multi-line invoice. Each line links to a source quote (base or
  // variation). claim_pct defaults to 100% of remaining-on-quote, manager
  // can override per line.
  type InvoiceLine = {
    source_quote_id: string;
    quote_number:    string;
    quote_title:     string;
    quote_total_ex:  number;
    invoiced_ex:     number;          // already-invoiced before this draft
    remaining_ex:    number;          // quote_total_ex - invoiced_ex
    description:     string;          // editable, defaults to quote_title
    claim_pct:       number;          // 0-100, % of remaining_ex to claim NOW
    claim_amount_ex?: number | null;  // 1 Sep 2026 — typed $ (STOW value) wins over the %
    desc_touched?:   boolean;         // T-504b — true once the manager edits it
  };
  const [lines, setLines]     = useState<InvoiceLine[]>([]);
  const [claimReference, setClaimReference] = useState("");
  const [xeroRefEdit, setXeroRefEdit] = useState<string | null>(null);   // 1 Nov 2026 — typed over the built reference
  const [issueDate, setIssueDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  // After generate: the freshly-created client_invoices row, used to wire
  // the PDF-upload step (T-226).
  const [generatedInvoice, setGeneratedInvoice] = useState<{
    invoice_id: string; invoice_number: string;
  } | null>(null);
  const [notes, setNotes] = useState("");

  const { data: project, isLoading } = useQuery({
    queryKey: ["project_for_invoice", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      // T-124 — PO data now lives on the won quote, not the project. Read both
      // legacy columns (back-compat for projects without a source_quote_id)
      // AND the won quote, then prefer the quote when present.
      const { data, error } = await supabase
        .from("projects")
        .select(`
          project_id, project_code, project_name, contract_value,
          client_po_number, client_po_value, client_po_date,
          client_id, source_quote_id, source_bid_id,
          invoiced_to_date, paid_to_date
        `)
        .eq("project_id", projectId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;

      // Pull PO from the won quote when source_quote_id is set. Falls back to
      // the legacy project columns for older imports without a quote linkage.
      if (data.source_quote_id) {
        const { data: q } = await supabase
          .from("quotes")
          .select("client_po_number, client_po_amount_ex_gst, client_po_date")
          .eq("quote_id", data.source_quote_id)
          .maybeSingle();
        if (q?.client_po_number) {
          (data as any).client_po_number = q.client_po_number;
          (data as any).client_po_value  = q.client_po_amount_ex_gst;
          (data as any).client_po_date   = q.client_po_date;
        }
      }

      // Resolve client_id via project -> quote -> bid fallback chain.
      // Some projects in the DB don't have client_id set directly; the
      // invoice worksheet showed CLIENT — because of that.
      let clientId: string | null = data.client_id ?? null;

      if (!clientId && data.source_quote_id) {
        const { data: q } = await supabase
          .from("quotes")
          .select("client_id")
          .eq("quote_id", data.source_quote_id)
          .maybeSingle();
        clientId = (q as any)?.client_id ?? null;
      }

      if (!clientId && data.source_bid_id) {
        const { data: b } = await supabase
          .from("bids")
          .select("client_id")
          .eq("bid_id", data.source_bid_id)
          .maybeSingle();
        clientId = (b as any)?.client_id ?? null;
      }

      const [cRes, qRes] = await Promise.all([
        clientId
          ? supabase
              .from("clients")
              .select(
                "client_id, legal_name, trading_name, client_code, abn, " +
                "billing_email, accounts_contact_name, accounts_contact_email, " +
                "accounts_contact_phone, payment_terms_days"
              )
              .eq("client_id", clientId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        data.source_quote_id
          ? supabase
              .from("quotes")
              .select("quote_number, quote_title, subtotal_excl_gst, total_incl_gst")
              .eq("quote_id", data.source_quote_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      return {
        ...data,
        client: cRes.data ?? null,
        quote: qRes.data ?? null,
      } as any;
    },
  });

  // One-shot init of due date from the client's payment terms, once loaded.
  // We use a ref so manual user edits aren't overwritten on re-render.
  const didInitDueDate = useRef(false);
  useEffect(() => {
    if (didInitDueDate.current) return;
    if (!project?.client) return;
    const terms = Number(project.client.payment_terms_days ?? 30);
    const d = new Date();
    d.setDate(d.getDate() + terms);
    setDueDate(d.toISOString().slice(0, 10));
    didInitDueDate.current = true;
  }, [project?.client]);

  // T-229 — Pull every accepted quote (base + variations) for this project,
  // with how much has been invoiced against each. Drives the line picker.
  const { data: claimableQuotes = [] } = useQuery({
    queryKey: ["v_quote_invoiced_summary", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_quote_invoiced_summary")
        .select("*")
        .eq("project_id", projectId)
        .in("quote_status", ["accepted", "partially_invoiced", "invoiced"])
        .order("quote_type", { ascending: true })   // 'standard' before 'variation'
        .order("quote_number", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{
        quote_id: string; quote_number: string; quote_title: string;
        quote_type: string; parent_quote_id: string | null;
        quote_total_ex_gst: number; invoiced_ex_gst: number;
        remaining_ex_gst: number; invoiced_pct: number;
      }>;
    },
  });

  // T-229 — Auto-add ALL claimable quotes as lines on first load (each at
  // 100% of remaining). Manager can untick / adjust before generating. This
  // matches James's stated workflow: typically Q + all accepted VARs together.
  const didInitLines = useRef(false);
  useEffect(() => {
    if (didInitLines.current) return;
    if (!claimableQuotes.length) return;
    setLines(
      claimableQuotes
        .filter(q => q.remaining_ex_gst > 0)
        .map(q => ({
          source_quote_id: q.quote_id,
          quote_number:    q.quote_number,
          quote_title:     q.quote_title,
          quote_total_ex:  Number(q.quote_total_ex_gst ?? 0),
          invoiced_ex:     Number(q.invoiced_ex_gst ?? 0),
          remaining_ex:    Number(q.remaining_ex_gst ?? 0),
          description:     defaultLineDescription(q.quote_number, q.quote_title, 100),
          claim_pct:       100,
        }))
    );
    didInitLines.current = true;
  }, [claimableQuotes]);

  // Previous claims on this project
  // T-229 — Read past claims directly from client_invoices (the new home).
  // Legacy invoice_register table is empty/missing in this DB so the old
  // query returned nothing; switching to client_invoices surfaces newly-
  // generated invoices including DRAFTs.
  const { data: pastClaims = [] } = useQuery({
    queryKey: ["project_past_claims", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await supabase
        .from("client_invoices")
        .select("invoice_id, invoice_number, total_incl_gst, invoice_date, status, sent_at, paid_at, pdf_file_id")
        .eq("project_id", projectId!)
        .order("invoice_date", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  // T-504b — composed once, shown on screen, saved on generate, sent by the
  // Worker. There is no second version of this string anywhere.
  const builtXeroReference = buildXeroReference({
    clientPo:       (project as any)?.client_po_number ?? null,
    jobName:        (project as any)?.site_name ?? project?.project_name ?? null,
    quoteNumbers:   lines.map(l => l.quote_number),
  });
  const xeroReference = (xeroRefEdit ?? builtXeroReference).slice(0, 255);

  // T-229 — Per-line claim amount = remaining × claim_pct / 100.
  // 1 Sep 2026 - James: "can't just be a %, sometimes I need to match an
  // invoice to a set STOW value". A typed dollar amount wins when present
  // (capped at what's left on the quote); the % is kept in step for display.
  function lineAmountExGst(l: InvoiceLine): number {
    if (l.claim_amount_ex != null && Number.isFinite(l.claim_amount_ex)) {
      return +Math.max(0, Math.min(l.claim_amount_ex, l.remaining_ex)).toFixed(2);
    }
    return +(l.remaining_ex * (l.claim_pct / 100)).toFixed(2);
  }
  const claimAmountExGst = +lines.reduce((sum, l) => sum + lineAmountExGst(l), 0).toFixed(2);
  const claimGst         = +(claimAmountExGst * 0.10).toFixed(2);
  const claimIncGst      = +(claimAmountExGst + claimGst).toFixed(2);

  // T-229 — Generate creates the invoice in OUR system (client_invoices +
  // client_invoice_lines), assigns the INV-YY-NNNNN number, and updates each
  // source quote's status. Manager THEN copies fields into Xero, comes back
  // and uploads the Xero PDF (T-226).
  const generateMut = useMutation({
    mutationFn: async () => {
      if (!project)              throw new Error("Project not loaded");
      if (!project.client)       throw new Error("Client not linked to project");
      if (lines.length === 0)    throw new Error("Add at least one line");
      if (claimAmountExGst <= 0) throw new Error("Total claim amount is $0 — adjust line %s");

      // 1. Insert invoice header. invoice_number auto-generated by DB default.
      //    quote_id on header = primary source quote (first line) for back-compat;
      //    real source-of-truth is per-line source_quote_id.
      const primarySourceQuoteId = lines[0].source_quote_id;
      const { data: invHeader, error: iErr } = await supabase
        .from("client_invoices")
        .insert({
          client_id:          project.client.client_id,
          project_id:         project.project_id,
          quote_id:           primarySourceQuoteId,
          subtotal_excl_gst:  claimAmountExGst,
          gst_amount:         claimGst,
          total_incl_gst:     claimIncGst,
          invoice_date:       issueDate,
          due_date:           dueDate || null,
          status:             "draft",
          // T-504b — all three of these were typed on this screen and thrown
          // away. invoice_title is the claim milestone, xero_reference is the
          // exact string Xero will receive, notes never leave Coretel.
          invoice_title:      claimReference.trim() || null,
          xero_reference:     xeroReference || null,
          notes:              notes.trim() || null,
        })
        .select("invoice_id, invoice_number")
        .single();
      if (iErr) throw new Error(`Could not create invoice header: ${iErr.message}`);

      // 1 Nov 2026 — our number no longer joins the reference; it becomes the
      // Xero invoice number itself when the worker pushes.

      // 2. Insert each line.
      const lineRows = lines.map((l, ix) => ({
        invoice_id:      invHeader.invoice_id,
        line_number:     ix + 1,
        description:     l.description,
        quantity:        1,
        unit_price:      lineAmountExGst(l),
        line_total:      lineAmountExGst(l),
        source_quote_id: l.source_quote_id,
        sort_order:      ix + 1,
      }));
      const { error: lErr } = await supabase
        .from("client_invoice_lines")
        .insert(lineRows);
      if (lErr) {
        // Roll back header so we don't orphan it
        await supabase.from("client_invoices").delete().eq("invoice_id", invHeader.invoice_id);
        throw new Error(`Could not create invoice lines: ${lErr.message}`);
      }

      // 3. Update each source quote's status. If the line claimed all the
      //    remaining → 'invoiced'. Else 'partially_invoiced'.
      for (const l of lines) {
        const afterThis = +(l.remaining_ex - lineAmountExGst(l)).toFixed(2);
        const newStatus = afterThis <= 0.01 ? "invoiced" : "partially_invoiced";
        const { error: qErr } = await supabase
          .from("quotes")
          .update({ status: newStatus })
          .eq("quote_id", l.source_quote_id);
        if (qErr) console.warn(`Could not update quote ${l.quote_number} status:`, qErr);
      }

      return invHeader as { invoice_id: string; invoice_number: string };
    },
    onSuccess: (invHeader) => {
      setGeneratedInvoice(invHeader);
      qc.invalidateQueries({ queryKey: ["v_quote_invoiced_summary", projectId] });
      qc.invalidateQueries({ queryKey: ["project_past_claims", projectId] });
    },
  });

  // T-504b — push straight from the worksheet, immediately after generate.
  const [wsPushed, setWsPushed] = useState<{ xero_number: string | null; pdf: string } | null>(null);
  const [wsBlockers, setWsBlockers] = useState<string[]>([]);
  const wsPushMut = useMutation({
    mutationFn: async () => {
      if (!generatedInvoice) throw new Error("Generate the invoice first");
      setWsBlockers([]);
      const res = await apiFetch("/xero/ar/push", {
        method: "POST",
        body: JSON.stringify({ invoice_id: generatedInvoice.invoice_id }),
      });
      const j = await res.json().catch(() => ({} as any));
      if (res.status === 409 && j?.blockers) { setWsBlockers(j.blockers); throw new Error("blocked"); }
      if (!res.ok) throw new Error((j?.messages?.join(" | ") || j?.detail || j?.error) ?? `Push failed (${res.status})`);
      return j;
    },
    onSuccess: (j: any) => {
      setWsPushed({ xero_number: j?.xero_number ?? null, pdf: j?.pdf ?? "unknown" });
      qc.invalidateQueries({ queryKey: ["project_past_claims", projectId] });
      qc.invalidateQueries({ queryKey: ["invoices_list"] });
    },
    onError: (e: any) => { if (e?.message !== "blocked") window.alert(`Couldn't push to Xero: ${e?.message ?? e}`); },
  });

  // T-226 — After Xero generates the PDF, manager uploads it and we attach
  // to client_invoices.pdf_file_id + flip status to 'sent'.
  const markSentMut = useMutation({
    mutationFn: async (pdfFile: File) => {
      if (!generatedInvoice) throw new Error("Generate the invoice first");
      // a. Sign + PUT the PDF to R2 via api-gateway (uses VITE_API_BASE_URL
      //    + JWT via the existing apiFetch helper).
      const objectKey = `client_invoices/${generatedInvoice.invoice_id}/${pdfFile.name}`;
      const signRes = await apiFetch("/sign-upload", {
        method: "POST",
        body: JSON.stringify({
          bucket:             "coretel-prod",
          object_key:         objectKey,
          content_type:       pdfFile.type || "application/pdf",
          expires_in_seconds: 900,
        }),
      });
      if (!signRes.ok) {
        const err = await signRes.text().catch(() => "");
        throw new Error(`sign-upload failed (${signRes.status}): ${err.slice(0, 200)}`);
      }
      const { upload_url } = await signRes.json();
      const putRes = await fetch(upload_url, {
        method:  "PUT",
        headers: { "Content-Type": pdfFile.type || "application/pdf" },
        body:    pdfFile,
      });
      if (!putRes.ok) throw new Error(`R2 PUT failed: ${putRes.status}`);

      // b. Create files row, link via pdf_file_id, mark sent
      const { data: fileRow, error: fErr } = await supabase
        .from("files")
        .insert({
          r2_bucket:       "coretel-prod",
          r2_object_key:   objectKey,
          file_name:       pdfFile.name,
          file_size_bytes: pdfFile.size,
          content_type:    pdfFile.type || "application/pdf",
          parent_table:    "client_invoices",
          parent_id:       generatedInvoice.invoice_id,
        })
        .select("file_id")
        .single();
      if (fErr) throw new Error(`Could not record file: ${fErr.message}`);

      const { error: uErr } = await supabase
        .from("client_invoices")
        .update({
          pdf_file_id: fileRow.file_id,
          sent_at:     new Date().toISOString(),
          status:      "sent",
        })
        .eq("invoice_id", generatedInvoice.invoice_id);
      if (uErr) throw new Error(`Could not mark sent: ${uErr.message}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project"] });
      qc.invalidateQueries({ queryKey: ["project_past_claims", projectId] });
      qc.invalidateQueries({ queryKey: ["invoices_list"] });
      navigate(`/projects/${projectId}`);
    },
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner/></div>;
  if (!project) return <div className="p-6">Project not found.</div>;

  // Pick the best contact email to show — accounts contact wins over billing.
  const xeroEmail =
    project.client?.accounts_contact_email ??
    project.client?.billing_email ??
    "";

  return (
    <div className="max-w-[1100px] mx-auto p-4 md:p-6">
      <button onClick={() => navigate(`/projects/${projectId}`)} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft size={14}/> Back to project
      </button>

      <h1 className="text-2xl font-semibold tracking-tight mb-1">
        Invoice worksheet
      </h1>
      <div className="text-text-muted text-[13px] mb-5">
        Build the claim here, then push it straight to Xero. Xero assigns the invoice
        number the client sees and sends its PDF back — you don't need to open Xero.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
        {/* Left: worksheet */}
        <div className="space-y-4">
          {/* T-229 — Multi-line invoice: pick which quotes/variations to bill */}
          <Card>
            <div className="p-5">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-3">
                <DollarSign size={11} className="inline mr-1 align-[-1px]"/>
                Invoice lines
              </div>

              {/* Available quotes/variations not yet on the invoice */}
              {claimableQuotes.length === 0 ? (
                <div className="text-[12px] text-text-muted py-2">
                  No accepted quotes for this project yet.
                </div>
              ) : (
                <div className="mb-4">
                  <div className="text-[11px] text-text-muted mb-2">Quotes &amp; variations on this project</div>
                  <div className="border border-line rounded">
                    {claimableQuotes.map(q => {
                      const onInvoice = lines.some(l => l.source_quote_id === q.quote_id);
                      const fullyClaimed = q.remaining_ex_gst <= 0.01;
                      return (
                        <div
                          key={q.quote_id}
                          className={`flex items-center gap-3 px-3 py-2 border-b border-line last:border-0 ${
                            fullyClaimed ? "opacity-50" : "cursor-pointer hover:bg-ink-raised"
                          }`}
                          onClick={() => {
                            if (fullyClaimed) return;
                            if (onInvoice) {
                              setLines(ls => ls.filter(l => l.source_quote_id !== q.quote_id));
                            } else {
                              setLines(ls => [...ls, {
                                source_quote_id: q.quote_id,
                                quote_number:    q.quote_number,
                                quote_title:     q.quote_title,
                                quote_total_ex:  Number(q.quote_total_ex_gst),
                                invoiced_ex:     Number(q.invoiced_ex_gst),
                                remaining_ex:    Number(q.remaining_ex_gst),
                                description:     defaultLineDescription(q.quote_number, q.quote_title, 100),
                                claim_pct:       100,
                              }]);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={onInvoice}
                            disabled={fullyClaimed}
                            onChange={() => {/* handled by row click */}}
                            className="cursor-pointer"
                          />
                          <span className="font-mono text-[11px] text-accent w-24">{q.quote_number}</span>
                          <span className="text-[12px] text-text flex-1 truncate">{q.quote_title}</span>
                          {q.quote_type === "variation" && (
                            <span className="text-[9px] uppercase font-mono px-1.5 py-0.5 rounded bg-accent/10 text-accent border border-accent/30">VAR</span>
                          )}
                          <span className="text-[11px] font-mono text-text-muted w-24 text-right">
                            {fmtMoney(q.quote_total_ex_gst)}
                          </span>
                          {q.invoiced_ex_gst > 0 ? (
                            <span className={`text-[11px] font-mono w-32 text-right ${fullyClaimed ? "text-ok" : "text-text-muted"}`}>
                              {fullyClaimed
                                ? "fully claimed"
                                : `${Math.round(q.invoiced_pct)}% claimed · ${fmtMoney(q.remaining_ex_gst)} left`}
                            </span>
                          ) : (
                            <span className="text-[11px] font-mono w-32 text-right text-text-dim">unclaimed</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Lines on this draft invoice */}
              {lines.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] text-text-muted mb-2">Lines on this invoice</div>
                  <div className="space-y-2">
                    {lines.map((l, ix) => (
                      <div key={l.source_quote_id} className="border border-line rounded p-3 bg-ink-raised/50">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] uppercase font-mono text-text-muted">Line {ix + 1}</span>
                          <span className="font-mono text-[11px] text-accent">{l.quote_number}</span>
                          <button
                            onClick={() => setLines(ls => ls.filter(x => x.source_quote_id !== l.source_quote_id))}
                            className="ml-auto text-text-muted hover:text-danger"
                            title="Remove line"
                          >
                            <Trash2 size={12}/>
                          </button>
                        </div>
                        <Field label="Description (editable)">
                          <Input value={l.description} onChange={(e: any) => {
                            const v = e.target.value;
                            setLines(ls => ls.map(x => x.source_quote_id === l.source_quote_id
                              ? { ...x, description: v, desc_touched: true } : x));
                          }}/>
                        </Field>
                        <div className="grid grid-cols-3 gap-3 mt-2 text-[11px] font-mono">
                          <Field label="Claim % of remaining">
                            <Input
                              type="number"
                              min="0" max="100" step="0.01"
                              mono
                              value={String(l.claim_pct)}
                              onChange={(e: any) => {
                                const n = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                setLines(ls => ls.map(x => x.source_quote_id === l.source_quote_id
                                  ? {
                                      ...x,
                                      claim_pct: n,
                                      claim_amount_ex: null,            // % is in charge again
                                      description: x.desc_touched
                                        ? x.description
                                        : defaultLineDescription(x.quote_number, x.quote_title, n),
                                    }
                                  : x));
                              }}
                            />
                          </Field>
                          <div>
                            <div className="text-[10px] uppercase text-text-dim mb-1">Remaining</div>
                            <div className="text-text-muted">{fmtMoney(l.remaining_ex)}</div>
                          </div>
                          {/* Type the $ here (e.g. the STOW value) and the % follows. */}
                          <Field label="Line ex GST">
                            <Input
                              type="number"
                              min="0" max={String(l.remaining_ex)} step="0.01"
                              mono
                              value={l.claim_amount_ex != null ? String(l.claim_amount_ex) : String(lineAmountExGst(l))}
                              onChange={(e: any) => {
                                const raw = e.target.value;
                                const amt = Math.max(0, Math.min(l.remaining_ex, Number(raw) || 0));
                                const pct = l.remaining_ex > 0 ? +((amt / l.remaining_ex) * 100).toFixed(3) : 0;
                                setLines(ls => ls.map(x => x.source_quote_id === l.source_quote_id
                                  ? {
                                      ...x,
                                      claim_amount_ex: raw === "" ? null : amt,
                                      claim_pct: pct,
                                      description: x.desc_touched
                                        ? x.description
                                        : defaultLineDescription(x.quote_number, x.quote_title, pct),
                                    }
                                  : x));
                              }}
                            />
                          </Field>
                        </div>

                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Optional invoice-level reference / milestone tag */}
              <Field label="Claim reference / milestone (optional)" hint="Shown in Xero invoice reference. e.g. 'Progress claim 1', 'Mob 50%'">
                <Input value={claimReference} onChange={e => setClaimReference(e.target.value)}
                  placeholder="e.g. Progress claim 1, Mobilisation 50%"/>
              </Field>

              {/* Totals */}
              <div className="mt-4 grid grid-cols-3 gap-3 bg-ink-raised rounded p-3 border border-line">
                <div>
                  <div className="text-[10px] uppercase text-text-muted font-mono">Ex GST</div>
                  <div className="text-[18px] font-mono text-text mt-1">{fmtMoney(claimAmountExGst)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-text-muted font-mono">GST</div>
                  <div className="text-[18px] font-mono text-text-muted mt-1">{fmtMoney(claimGst)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-text-muted font-mono">Total inc GST</div>
                  <div className="text-[18px] font-mono text-accent mt-1">{fmtMoney(claimIncGst)}</div>
                </div>
              </div>
            </div>
          </Card>

          {/* T-504b — this is no longer a copy-and-paste crib sheet. It is the
              actual payload: every value below is what POST /xero/ar/push sends. */}
          <Card>
            <div className="p-5">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-1">
                What Xero will receive
              </div>
              <div className="text-[11px] text-text-muted mb-3">
                Exactly these values are sent when you push. Copy buttons are still here if
                you ever need to key one in by hand.
              </div>

              <div className="space-y-2.5">
                <CopyRow label="Contact"        value={project.client?.legal_name ?? "—"}/>
                <CopyRow
                  label="Email to"
                  value={xeroEmail || "—"}
                  hint="From client's Accounts contact. Edit on the Clients screen."
                />
                <CopyRow label="Issue date"     value={issueDate} copyValue={formatDateForXero(issueDate)} editable onChange={v => setIssueDate(v)} type="date"/>
                <CopyRow label="Due date"       value={dueDate}   copyValue={formatDateForXero(dueDate)}   editable onChange={v => { didInitDueDate.current = true; setDueDate(v); }}   type="date"/>
                {/* T-504b — buildXeroReference, the one and only builder.
                    1 Nov 2026 — editable here; our INV number is Xero's invoice
                    number now, never part of the reference. */}
                <CopyRow
                  label="Reference"
                  value={xeroReference}
                  editable
                  onChange={v => setXeroRefEdit(v)}
                  hint="Client PO / STOW, quote, site. Type over it if the client wants something else. Our INV number becomes the Xero invoice number."
                />
                <CopyRow label="Invoice number" value="Ours (INV-YY-NNNNN), assigned on generate" hint="Xero uses our number — one number on the client's copy, the remittance and Ops."/>
                <CopyRow label="Amounts are"    value="Tax exclusive"/>
                <CopyRow label="Posts to"       value="200 Sales · GST on Income" hint="Every line. Change it in Xero and re-sync the chart of accounts."/>
                <CopyRow label="Status in Xero" value="Authorised" hint="Lands on the aged receivables report straight away."/>

                {/* T-229 — One LINE block per invoice line */}
                {lines.length === 0 ? (
                  <div className="text-[12px] text-text-muted italic px-1 py-2">
                    Add at least one line above to populate Xero LINE fields.
                  </div>
                ) : (
                  lines.map((l, ix) => (
                    <div key={l.source_quote_id} className="border-t border-line pt-2.5 mt-2.5 first:border-0 first:pt-0 first:mt-0 space-y-2.5">
                      <div className="text-[10px] uppercase tracking-wider2 text-text-dim font-mono">
                        Xero LINE {ix + 1} · {l.quote_number}
                      </div>
                      <CopyRow label="Description" value={l.description}/>
                      <CopyRow label="Qty"         value="1"/>
                      <CopyRow label="Price"       value={lineAmountExGst(l).toFixed(2)}/>
                      <CopyRow label="Tax rate"    value="GST on Income (10%)"/>
                    </div>
                  ))
                )}

                <div className="border-t border-line pt-2.5 mt-2.5">
                  <CopyRow label="Total inc GST" value={claimIncGst.toFixed(2)} hint="Xero calculates this — use to verify"/>
                </div>
              </div>
            </div>
          </Card>

          {/* Notes / memo */}
          <Card>
            <div className="p-5">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2">Notes (optional)</div>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Internal notes about this claim — not sent to client"
                className="w-full bg-ink border border-line rounded p-2 text-[13px] text-text min-h-[80px] outline-none focus:border-accent/40"
              />
            </div>
          </Card>

          {/* T-229 / T-226 — Generate invoice in our system → open Xero → upload PDF back */}
          <Card>
            <div className="p-5">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-3">
                {generatedInvoice ? "After creating in Xero" : "Generate invoice"}
              </div>

              {!generatedInvoice ? (
                <>
                  <div className="text-[12px] text-text-muted mb-3">
                    Creates invoice in our system + assigns INV number. You then copy fields to Xero, save in Xero,
                    download Xero's PDF, and upload it back here to finalise.
                  </div>

                  {generateMut.isError && (
                    <div className="mb-3 bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
                      <AlertTriangle size={11} className="inline mr-1"/>
                      {(generateMut.error as any)?.message}
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-3 border-t border-line">
                    <Button variant="secondary" onClick={() => navigate(`/projects/${projectId}`)}>Cancel</Button>
                    <Button
                      icon={FileText}
                      onClick={() => generateMut.mutate()}
                      disabled={generateMut.isPending || lines.length === 0 || claimAmountExGst <= 0}
                    >
                      {generateMut.isPending ? "Generating…" : "Generate invoice"}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-ok/10 border border-ok/30 rounded px-3 py-2 mb-3 text-[12px]">
                    <CheckCircle2 size={12} className="inline mr-1 text-ok"/>
                    Created <span className="font-mono text-text">{generatedInvoice.invoice_number}</span> · status DRAFT
                  </div>

                  {/* T-504b — one button finishes the job. */}
                  {wsPushed ? (
                    <div className="bg-ok/10 border border-ok/30 rounded px-3 py-2.5 mb-4 text-[12px] space-y-1">
                      <div className="text-ok">
                        <CheckCircle2 size={12} className="inline mr-1"/>
                        In Xero as <span className="font-mono text-text">{wsPushed.xero_number ?? "an invoice"}</span>
                      </div>
                      <div className="text-text-muted">
                        {wsPushed.pdf === "ok"
                          ? "Xero's PDF has been saved against the invoice — open it from Finance → Invoices to download or send."
                          : "The invoice is in Xero but its PDF didn't come back. Don't push again — download it from Xero and upload it below."}
                      </div>
                      <Link to="/invoices" className="text-accent hover:underline inline-block">Go to invoices →</Link>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <Button icon={Send} onClick={() => wsPushMut.mutate()} disabled={wsPushMut.isPending}>
                        {wsPushMut.isPending ? "Pushing to Xero…" : "Push to Xero"}
                      </Button>
                      <div className="text-[11px] text-text-muted mt-1.5">
                        Creates the invoice in Xero and brings its PDF back here.
                      </div>
                      {wsBlockers.length > 0 && (
                        <div className="mt-2 bg-warning/10 border border-warning/30 rounded px-3 py-2 text-[12px] text-warning">
                          <div className="font-medium mb-1">Not ready to push:</div>
                          <ul className="list-disc pl-4 space-y-0.5">
                            {wsBlockers.map((b, i) => <li key={i}>{b}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <Field label="Upload Xero PDF" hint="Only needed if the push couldn't bring the PDF back. Normally this fills itself.">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) markSentMut.mutate(f);
                      }}
                      disabled={markSentMut.isPending}
                      className="block w-full text-[12px] text-text file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-line file:bg-ink-raised file:text-text hover:file:bg-ink-card"
                    />
                  </Field>

                  {markSentMut.isError && (
                    <div className="mt-3 bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
                      <AlertTriangle size={11} className="inline mr-1"/>
                      {(markSentMut.error as any)?.message}
                    </div>
                  )}

                  {markSentMut.isPending && (
                    <div className="mt-3 text-[12px] text-text-muted">
                      <Spinner/> Uploading PDF + marking sent…
                    </div>
                  )}

                  <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-line">
                    <Button variant="secondary" onClick={() => navigate(`/projects/${projectId}`)}>
                      Skip — finish later
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>

        {/* Right: project context panel */}
        <div className="space-y-4">
          <Card>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2">Project</div>
              <div className="font-mono text-[13px] text-accent">{project.project_code}</div>
              <div className="text-[13px] text-text">{project.project_name}</div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2">Client</div>
              {project.client ? (
                <>
                  <div className="text-[13px] text-text">{project.client.legal_name}</div>
                  {project.client.abn && (
                    <div className="text-[11px] font-mono text-text-muted mt-1">ABN {project.client.abn}</div>
                  )}
                  {project.client.accounts_contact_name && (
                    <div className="text-[11px] text-text-muted mt-2">
                      <span className="text-text-dim">Accounts: </span>
                      {project.client.accounts_contact_name}
                    </div>
                  )}
                  {project.client.accounts_contact_email && (
                    <div className="text-[11px] mt-0.5">
                      <a
                        href={`mailto:${project.client.accounts_contact_email}`}
                        className="text-accent hover:text-accent-bright"
                      >
                        {project.client.accounts_contact_email}
                      </a>
                    </div>
                  )}
                  {!project.client.accounts_contact_email && project.client.billing_email && (
                    <div className="text-[11px] text-text-muted mt-0.5">{project.client.billing_email}</div>
                  )}
                  {project.client.payment_terms_days != null && (
                    <div className="text-[10px] text-text-dim mt-2 font-mono">
                      Terms: {project.client.payment_terms_days} days
                    </div>
                  )}
                  {project.client.client_id && (
                    <Link
                      to={`/clients/${project.client.client_id}`}
                      className="text-[11px] text-accent hover:text-accent-bright mt-2 inline-block"
                    >
                      View client →
                    </Link>
                  )}
                </>
              ) : (
                <div className="text-[12px] text-text-dim">
                  No client linked to this project.{" "}
                  <Link to="/clients" className="text-accent hover:text-accent-bright">Manage clients →</Link>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2">Client PO</div>
              {project.client_po_number ? (
                <>
                  <div className="font-mono text-[13px] text-text">{project.client_po_number}</div>
                  <div className="text-[11px] text-text-muted mt-1">{fmtMoney(project.client_po_value)} · {fmtDate(project.client_po_date)}</div>
                </>
              ) : <div className="text-[12px] text-text-dim">Not set</div>}
            </div>
          </Card>

          {pastClaims.length > 0 && (
            <Card>
              <div className="p-4">
                <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-semibold mb-2">Past claims</div>
                <div className="space-y-1.5">
                  {pastClaims.map((c: any) => (
                    <button type="button" key={c.invoice_id} onClick={() => setOpenPast(c.invoice_id)} title="Open this invoice"
                      className="w-full text-[11px] flex items-center justify-between gap-2 rounded px-1 -mx-1 py-0.5 hover:bg-ink-raised/70 text-left">
                      <span className="font-mono text-accent shrink-0 hover:underline">{c.invoice_number}</span>
                      <span className={`text-[9px] uppercase font-mono px-1.5 rounded ${
                        c.status === "paid"   ? "bg-ok/10 text-ok border border-ok/30" :
                        c.status === "sent"   ? "bg-accent/10 text-accent border border-accent/30" :
                        c.status === "draft"  ? "bg-warning/10 text-warning border border-warning/30" :
                                                "bg-ink-raised text-text-muted border border-line"
                      }`}>{c.status}</span>
                      <span className="text-text-muted ml-auto">{fmtMoney(c.total_incl_gst)}</span>
                    </button>
                  ))}
                </div>
              </div>
              {openPast && (
                <InvoiceDetailModal invoiceId={openPast} onClose={() => setOpenPast(null)}
                  onChanged={() => { qc.invalidateQueries({ queryKey: ["project_past_claims", projectId] }); qc.invalidateQueries({ queryKey: ["invoices_list"] }); }}/>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Copy-to-clipboard row
// -----------------------------------------------------------------------------
function CopyRow({ label, value, editable, onChange, type, hint, copyValue }: {
  label: string;
  value: string;
  editable?: boolean;
  onChange?: (v: string) => void;
  type?: string;
  hint?: string;
  // Optional override for what gets copied to clipboard. Used for date inputs:
  // the input itself needs ISO yyyy-mm-dd to render, but we want to copy a
  // pretty Xero-friendly format like "7 May 2026". Falls back to `value`.
  copyValue?: string;
}) {
  const [copied, setCopied] = useState(false);
  async function doCopy() {
    try {
      await navigator.clipboard.writeText(copyValue ?? value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard permissions — ignore */ }
  }

  return (
    <div className="grid grid-cols-[140px_1fr_auto] gap-3 items-center">
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium">{label}</div>
      {editable ? (
        <input
          type={type ?? "text"}
          value={value}
          onChange={e => onChange?.(e.target.value)}
          className="bg-ink-raised border border-line rounded px-2 py-1 text-[13px] text-text font-mono outline-none focus:border-accent/40"
        />
      ) : (
        <div className="bg-ink-raised border border-line rounded px-2 py-1 text-[13px] text-text font-mono">{value || "—"}</div>
      )}
      <button
        onClick={doCopy}
        className={`px-2 py-1 rounded border text-[11px] inline-flex items-center gap-1 transition-colors ${
          copied ? "bg-ok/10 text-ok border-ok/30" : "bg-ink-raised text-text-muted border-line hover:text-text"
        }`}
      >
        <Copy size={10}/> {copied ? "copied" : "copy"}
      </button>
      {hint && <div className="col-start-2 text-[10px] text-text-dim italic">{hint}</div>}
    </div>
  );
}

function formatDateForXero(iso: string): string {
  // Xero expects d MMM yyyy — e.g., "22 Apr 2026"
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

// =============================================================================
// Invoices list — all invoices across all projects (AR view)
// =============================================================================
export function InvoicesListScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const isDirector = hasRole(user, "director");
  // T-237 — click row → opens detail modal (replaces resume-draft + mark-paid gaps)
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);   // T-346
  const [reconcileOpen, setReconcileOpen] = useState(false); // T-504

  // Director-only delete for sent-but-not-paid AR invoices.
  // James 2026-04-26: useful for clearing test/aborted invoices without
  // resorting to SQL. Only enabled when status is NOT 'paid' — once Xero
  // has marked something paid, that's a real money record and shouldn't
  // be a one-click delete.
  const deleteMut = useMutation({
    mutationFn: async (inv: { invoice_number: string }) => {
      // The invoice_register view's `id` column ISN'T the underlying
      // client_invoices.invoice_id (it's a row-number-ish thing). Look up the
      // real UUID via invoice_number first, then delete by that.
      const { data: realRow, error: e0 } = await supabase
        .from("client_invoices")
        .select("invoice_id")
        .eq("invoice_number", inv.invoice_number)
        .maybeSingle();
      if (e0) throw new Error(`Lookup failed: ${e0.message}`);
      if (!realRow) throw new Error(`Invoice ${inv.invoice_number} not found in client_invoices — may live in a different table the view aggregates from. Tell James and we'll trace it.`);

      // Cascade: child lines first, then header
      const { error: e1 } = await supabase
        .from("client_invoice_lines")
        .delete()
        .eq("invoice_id", realRow.invoice_id);
      if (e1) throw new Error(`Could not delete invoice lines: ${e1.message}`);
      const { error: e2 } = await supabase
        .from("client_invoices")
        .delete()
        .eq("invoice_id", realRow.invoice_id);
      if (e2) {
        const parts = [e2.message, (e2 as any).details, (e2 as any).hint].filter(Boolean);
        throw new Error(parts.join(" — "));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices_list"] });
      qc.invalidateQueries({ queryKey: ["invoices_kpi"] });
    },
    onError: (e: any) => window.alert(`Delete failed: ${e?.message ?? "unknown"}`),
  });

  function handleDelete(e: React.MouseEvent, inv: any) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete ${inv.invoice_number}?\n\nAmount: ${fmtMoney(inv.invoice_amount)}\nStatus: ${inv.invoice_status}\n\nThis can't be undone.`)) return;
    deleteMut.mutate({ invoice_number: inv.invoice_number });
  }

  // T-229 — Source of truth is client_invoices (with joins to projects + clients
  // for the display-friendly project_code / client_name shown in the list).
  const { data: kpi } = useQuery({
    queryKey: ["invoices_kpi"],
    queryFn: async () => {
      const { data } = await supabase
        .from("client_invoices")
        .select("status, total_incl_gst, amount_paid");
      const rows = (data ?? []) as any[];
      const sumExceptVoid = (filterFn: (s: string) => boolean) =>
        rows.filter(r => filterFn(r.status)).reduce((a, r) => a + Number(r.total_incl_gst ?? 0), 0);
      // "Issued" = invoices actually sent to a client — a draft isn't issued yet,
      // so it's excluded here (and from Outstanding), which keeps the headline
      // reconciling: Issued = Paid + Outstanding. Drafts still show in the table.
      // T-346 — part-paid invoices contribute their allocated amount to Paid,
      // and only the unpaid remainder to Outstanding, so the identity holds.
      const isLive = (s: string) => s !== "void" && s !== "draft";
      const partPaidAmt = rows.filter(r => r.status === "part_paid")
        .reduce((a, r) => a + Number(r.amount_paid ?? 0), 0);
      return {
        total_issued:      sumExceptVoid(isLive),
        total_paid:        sumExceptVoid(s => s === "paid") + partPaidAmt,
        total_outstanding: sumExceptVoid(s => isLive(s) && s !== "paid") - partPaidAmt,
        count:             rows.length,
      };
    },
  });

  const { data: allInvoices = [], isLoading, error } = useQuery({
    queryKey: ["invoices_list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_invoices")
        .select(`
          invoice_id, invoice_number, total_incl_gst, amount_paid, invoice_date, due_date,
          status, sent_at, paid_at, pdf_file_id, external_invoice_number, external_reference,
          projects ( project_id, project_code, project_name, sites ( site_name ) ),
          clients  ( legal_name, trading_name )
        `)
        .order("invoice_date", { ascending: false, nullsFirst: false })
        .limit(200);
      if (error) throw error;
      // Flatten the joined fields to match the UI's existing shape
      return (data ?? []).map((row: any) => ({
        invoice_id:          row.invoice_id,
        invoice_number:      row.invoice_number,
        invoice_amount:      row.total_incl_gst,
        invoice_date_sent:   row.invoice_date,
        invoice_due:         row.due_date,
        invoice_status:      row.status,
        paid_date:           row.paid_at,
        // T-346 — part-paid: outstanding = total minus allocations.
        balance_outstanding: row.status === "paid" ? 0
          : Math.max(0, Number(row.total_incl_gst ?? 0) - Number(row.amount_paid ?? 0)),
        amount_paid:         row.status === "paid" ? row.total_incl_gst : Number(row.amount_paid ?? 0),
        project_id:          row.projects?.project_id ?? null,
        project_code:        row.projects?.project_code,
        site_name:           row.projects?.sites?.site_name ?? null,
        client_name:         row.clients?.legal_name,
        client_trading:      row.clients?.trading_name ?? null,
        external_invoice_number: row.external_invoice_number ?? null,
        external_reference:  row.external_reference ?? null,
        pdf_file_id:         row.pdf_file_id,
      }));
    },
  });

  // T-410 — search was server-ilike on invoice_number ONLY, so typing a client
  // name showed "No invoices". Now filters everything visible client-side:
  // invoice #, client, project, site, and the RCTI external number/refs.
  const todayIso = new Date().toISOString().slice(0, 10);
  const in30Iso = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

  const invoices = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allInvoices.filter((inv: any) => {
      if (term && ![inv.invoice_number, inv.client_name, inv.client_trading, inv.project_code,
        inv.site_name, inv.external_invoice_number, inv.external_reference]
        .some(v => v && String(v).toLowerCase().includes(term))) return false;
      if (clientFilter !== "all" && inv.client_name !== clientFilter) return false;
      const st = String(inv.invoice_status ?? "").toLowerCase();
      if (statusFilter === "overdue") {
        return inv.balance_outstanding > 0 && inv.invoice_due && inv.invoice_due < todayIso
          && st !== "paid" && st !== "void" && st !== "draft";
      }
      if (statusFilter === "to_invoice") return true;   // the pill swaps the table for the claim bars; the tiles keep showing everything
      if (statusFilter !== "all") return st === statusFilter;
      return true;
    });
  }, [allInvoices, search, statusFilter, clientFilter, todayIso]);

  // T-465 — client filter options
  const clientList = useMemo(
    () => Array.from(new Set(allInvoices.map((i: any) => i.client_name).filter(Boolean))).sort(),
    [allInvoices]);

  // T-465 — KPI boxes reflect the filtered set
  const fkpi = useMemo(() => {
    const live = invoices.filter((i: any) => { const t = String(i.invoice_status ?? "").toLowerCase(); return t !== "void" && t !== "draft"; });
    return {
      issued:      live.reduce((a: number, i: any) => a + Number(i.invoice_amount ?? 0), 0),
      paid:        invoices.reduce((a: number, i: any) => a + Number(i.amount_paid ?? 0), 0),
      outstanding: invoices.reduce((a: number, i: any) => a + Number(i.balance_outstanding ?? 0), 0),
      count:       invoices.length,
    };
  }, [invoices]);

  // T-465 — outstanding by client (all live invoices) for the comparison donut
  const byClient = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of allInvoices as any[]) {
      const out = Number(i.balance_outstanding ?? 0);
      if (out <= 0) continue;
      const k = i.client_name ?? "—";
      m.set(k, (m.get(k) ?? 0) + out);
    }
    return Array.from(m.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [allInvoices]);
  const arTotal = useMemo(() => byClient.reduce((a, c) => a + c.value, 0), [byClient]);

  // 23 Oct 2026 (mig 449) — money we are owed that has no invoice yet. One row
  // per project from v_project_claim_position: contract (accepted quote +
  // approved variations + extras, ex GST) less what has been invoiced. James's
  // rule: the contract is the target, the PO is only a check — Ventia pay on
  // RCTI with no PO, and a PO often covers one extra, not the job.
  const { data: claimRows = [] } = useQuery({
    queryKey: ["ar_claim_position"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_project_claim_position")
        .select("*").order("still_to_claim_ex", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const toClaim = useMemo(() => {
    const rows = claimRows.filter((r: any) =>
      Math.abs(Number(r.still_to_claim_ex ?? 0)) >= 1 && (clientFilter === "all" || r.client_name === clientFilter));   // under a dollar is rounding, not money
    const under = rows.filter((r: any) => Number(r.still_to_claim_ex) > 0);
    const over  = rows.filter((r: any) => Number(r.still_to_claim_ex) < 0);
    const total = under.reduce((a: number, r: any) => a + Number(r.still_to_claim_ex), 0);
    return { under, over, total };
  }, [claimRows, clientFilter]);

  // T-465 — aging of outstanding: overdue (past) / due <=30d (current) / not yet due (future)
  const aging = useMemo(() => {
    let overdue = 0, dueSoon = 0, future = 0;
    for (const i of allInvoices as any[]) {
      const out = Number(i.balance_outstanding ?? 0);
      if (out <= 0) continue;
      const due = i.invoice_due;
      if (!due) { future += out; continue; }
      if (due < todayIso) overdue += out;
      else if (due <= in30Iso) dueSoon += out;
      else future += out;
    }
    return { overdue, dueSoon, future };
  }, [allInvoices, todayIso, in30Iso]);

  const CLIENT_COLORS = ["#F97316", "#10B981", "#3B82F6", "#A855F7", "#EAB308", "#EF4444", "#14B8A6", "#EC4899", "#6366F1", "#84CC16"];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Invoices (AR)</h1>
          <div className="text-text-muted text-[13px] mt-1">
            Client invoices issued via Xero. Create new ones from Project → Financials → Issue invoice.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* T-504 — Xero holds the bank feed, so this is how ops stops drifting
              away from it. Read-only until you press Apply. */}
          <Button variant="secondary" icon={ExternalLink} onClick={() => setReconcileOpen(true)}>
            Reconcile against Xero
          </Button>
          {/* T-346 — one bank credit spread across invoices (the HVAC lump problem) */}
          <Button icon={Banknote} onClick={() => setReceiptOpen(true)}>Record payment</Button>
        </div>
      </div>
      {receiptOpen && (
        <ArReceiptModal onClose={() => setReceiptOpen(false)} onSaved={() => setReceiptOpen(false)} />
      )}
      {reconcileOpen && (
        <XeroReconcileModal
          onClose={() => setReconcileOpen(false)}
          onApplied={() => {
            qc.invalidateQueries({ queryKey: ["invoices_list"] });
            qc.invalidateQueries({ queryKey: ["invoices_kpi"] });
          }}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <KPI label="Issued"      value={fmtMoney(fkpi.issued)} icon={FileText}/>
        <KPI label="Paid"        value={fmtMoney(fkpi.paid)} />
        <KPI label="Outstanding" value={fmtMoney(fkpi.outstanding)} accent />
        <button type="button" onClick={() => setStatusFilter(statusFilter === "to_invoice" ? "all" : "to_invoice")} className={`text-left rounded-lg ${statusFilter === "to_invoice" ? "ring-2 ring-accent/60" : ""}`} title="Work done that has no invoice yet — shows the projects">
          <KPI label="Still to invoice" value={fmtMoney(toClaim.total)} sub={`ex GST · ${toClaim.under.length} project${toClaim.under.length === 1 ? "" : "s"} not fully claimed`} icon={DollarSign} accent={statusFilter === "to_invoice"}/>
        </button>
        <KPI label="Count"       value={fkpi.count} />
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-ink-raised border border-line rounded-md flex-1 min-w-[220px] max-w-md">
          <input placeholder="Search invoice # or client..." value={search} onChange={e => setSearch(e.target.value)}
            className="bg-transparent outline-none text-text text-[13px] flex-1"/>
        </div>
        <div className="inline-flex border border-line rounded-md bg-ink-raised p-0.5 gap-0.5 flex-wrap">
          {([["all","All"],["sent","Sent"],["part_paid","Part paid"],["paid","Paid"],["overdue","Overdue"],["draft","Draft"],["void","Void"],["to_invoice","To invoice"]] as const).map(([v,label]) => (
            <button key={v} type="button" onClick={() => setStatusFilter(v)}
              className={`px-2.5 py-1 rounded text-[12px] ${statusFilter === v ? "bg-accent text-white" : "text-text-muted hover:text-text"}`}>{label}</button>
          ))}
        </div>
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          className="bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
          <option value="all">All clients</option>
          {clientList.map((c: any) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {byClient.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
          <Card>
            <div className="p-4">
              <div className="text-[13px] font-medium">Outstanding by client</div>
              <div className="text-[11px] text-text-muted mb-1">Total owed {fmtMoney(arTotal)}</div>
              <div style={{ width: "100%", height: 190 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={byClient} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={1}>
                      {byClient.map((_, i) => <Cell key={i} fill={CLIENT_COLORS[i % CLIENT_COLORS.length]} />)}
                    </Pie>
                    <RTooltip formatter={(val: any) => fmtMoney(Number(val))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
          <Card className="lg:col-span-2">
            <div className="p-4">
              <div className="text-[13px] font-medium mb-2">Owed per client</div>
              <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                {byClient.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-2 text-[12px]">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: CLIENT_COLORS[i % CLIENT_COLORS.length] }} />
                    <span className="text-text flex-1 truncate">{c.name}</span>
                    <span className="font-mono text-text-muted w-10 text-right">{arTotal > 0 ? Math.round((c.value / arTotal) * 100) : 0}%</span>
                    <span className="font-mono text-text w-24 text-right">{fmtMoney(c.value)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-line grid grid-cols-3 gap-2">
                <div><div className="text-[10px] uppercase tracking-wide text-text-muted">Overdue</div><div className="font-mono text-[13px] text-danger">{fmtMoney(aging.overdue)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-text-muted">Due ≤30d</div><div className="font-mono text-[13px] text-warn">{fmtMoney(aging.dueSoon)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-text-muted">Not yet due</div><div className="font-mono text-[13px] text-text-muted">{fmtMoney(aging.future)}</div></div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">
          <AlertTriangle size={12} className="inline mr-1.5"/>
          {(error as any).message}
        </div>
      )}

      {statusFilter === "to_invoice" ? (
        <Card>
          <div className="p-4">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
              <div className="text-[13px] font-medium">Still to invoice — work done, no invoice yet</div>
              <div className="text-[11px] text-text-muted flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-ok inline-block"/>paid</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-accent inline-block"/>invoiced, unpaid</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-ink-raised border border-line inline-block"/>to claim</span>
                <span className="text-text-dim">· ex GST · open jobs with a contract value · against the contract (quote + approved variations)</span>
              </div>
            </div>
            {toClaim.under.length + toClaim.over.length === 0
              ? <div className="text-[12px] text-text-muted py-4">Every live job is fully claimed.</div>
              : <ClaimBars rows={[...toClaim.under, ...toClaim.over]}/>}
          </div>
        </Card>
      ) : isLoading ? <div className="py-10 flex justify-center"><Spinner/></div>
       : invoices.length === 0 ? <Card><EmptyState title="No invoices" description="Create one from a Project's Financials tab." icon={FileText}/></Card>
       : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-line">
                  {["Invoice #", "Project / Site", "Client", "Sent", "Due", "Amount", "Paid", "Outstanding", "Status", ""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const isPaid = String(inv.invoice_status ?? "").toLowerCase() === "paid";
                    return (
                      <tr
                        key={inv.invoice_id}
                        className={`border-b border-line cursor-pointer hover:bg-ink-raised/50 ${i % 2 ? "bg-ink/40" : ""}`}
                        onClick={() => setOpenInvoiceId(inv.invoice_id)}
                      >
                        <td className="px-4 py-3 font-mono text-[11px] text-accent">
                          {inv.invoice_number}
                          {/* T-410 — client-issued RCTI number under ours */}
                          {inv.external_invoice_number && (
                            <div className="text-[10px] text-text-muted" title="Client-issued RCTI/TSL invoice number">
                              RCTI {inv.external_invoice_number}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-mono text-[11px] text-text-muted">{inv.project_code ?? "—"}</div>
                          {inv.site_name && <div className="text-[12px] text-text">{inv.site_name}</div>}
                        </td>
                        <td className="px-4 py-3 text-[12px]">{inv.client_name ?? "—"}</td>
                        <td className="px-4 py-3 font-mono text-[12px] text-text-muted">{fmtDate(inv.invoice_date_sent)}</td>
                        <td className={`px-4 py-3 font-mono text-[12px] ${inv.invoice_due && inv.invoice_due < todayIso && inv.balance_outstanding > 0 ? "text-danger" : "text-text-muted"}`}>{inv.invoice_due ? fmtDate(inv.invoice_due) : "—"}</td>
                        <td className="px-4 py-3 font-mono">{fmtMoney(inv.invoice_amount)}</td>
                        <td className="px-4 py-3 font-mono text-ok">{fmtMoney(inv.amount_paid)}</td>
                        <td className="px-4 py-3 font-mono">{fmtMoney(inv.balance_outstanding)}</td>
                        <td className="px-4 py-3">
                          <Pill
                            label={inv.invoice_status ?? "—"}
                            tone={isPaid ? "won" : inv.invoice_status === "part_paid" ? "mobilising" : inv.invoice_status === "void" ? "lost" : "pending"}
                            small
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isDirector && !isPaid && (
                            <button
                              type="button"
                              onClick={(e) => handleDelete(e, inv)}
                              title={`Delete ${inv.invoice_number} — director only, only when not paid`}
                              className="p-1.5 rounded text-text-dim hover:text-danger hover:bg-danger/10 transition-colors"
                              disabled={deleteMut.isPending}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

      {openInvoiceId && (
        <InvoiceDetailModal
          invoiceId={openInvoiceId}
          onClose={() => setOpenInvoiceId(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ["invoices_list"] });
            qc.invalidateQueries({ queryKey: ["invoices_kpi"] });
            qc.invalidateQueries({ queryKey: ["project_past_claims"] });
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ClaimBars — 23 Oct 2026. How far through each job's contract the claims are:
// one bar per project, grouped by client. Paid (green) · invoiced but unpaid
// (orange) · still to claim (empty tail). Over-claimed jobs get a red overflow.
// James: "the tables look a bit shit" — this is the picture instead.
// -----------------------------------------------------------------------------
function ClaimBars({ rows }: { rows: any[] }) {
  const groups = new Map<string, any[]>();
  for (const r of rows) {
    const k = r.client_trading ?? r.client_name ?? "—";
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(r);
  }
  const ordered = Array.from(groups.entries())
    .map(([name, rs]) => ({ name, rs, toClaim: rs.reduce((a, r) => a + Math.max(0, Number(r.still_to_claim_ex)), 0) }))
    .sort((a, b) => b.toClaim - a.toClaim);
  return (
    <div className="space-y-4 mt-2">
      {ordered.map(g => (
        <div key={g.name}>
          <div className="flex items-baseline justify-between gap-2 border-b border-line pb-1 mb-2">
            <div className="text-[12px] font-medium text-text">{g.name}</div>
            <div className="text-[12px] font-mono text-accent">{fmtMoney(g.toClaim)} <span className="text-text-muted font-sans text-[11px]">to claim</span></div>
          </div>
          <div className="space-y-2">
            {g.rs.map((r: any) => {
              const contract = Number(r.contract_value_ex), inv = Number(r.invoiced_ex), paid = Number(r.paid_ex);
              const stc = Number(r.still_to_claim_ex);
              const over = stc < 0;
              const scale = Math.max(contract, inv, 0.01);
              const pPaid = Math.min(paid, inv) / scale * 100;
              const pUnpaid = Math.max(0, inv - paid) / scale * 100;
              const pOver = over ? (inv - contract) / scale * 100 : 0;
              return (
                <div key={r.project_id} className="grid grid-cols-[minmax(150px,1.3fr)_minmax(0,3fr)_auto] items-center gap-3">
                  <div className="min-w-0">
                    <Link to={`/projects/${r.project_id}/invoice/new`} className="font-mono text-[11px] text-accent hover:underline" title="Open this project's invoice worksheet">{r.project_code}</Link>
                    <div className="text-[12px] text-text truncate">{r.site_name ?? r.project_name}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="h-3 rounded bg-ink-raised border border-line overflow-hidden flex" title={`contract ${fmtMoney(contract)} · invoiced ${fmtMoney(inv)} · paid ${fmtMoney(paid)}`}>
                      <div className="h-full bg-ok" style={{ width: `${pPaid}%` }}/>
                      <div className="h-full bg-accent" style={{ width: `${pUnpaid - pOver}%` }}/>
                      {over && <div className="h-full bg-danger" style={{ width: `${pOver}%` }}/>}
                    </div>
                    <div className="text-[10px] text-text-muted mt-0.5 font-mono">{fmtMoney(inv)} of {fmtMoney(contract)} claimed{r.po_count > 0 && Number(r.po_less_contract_ex) < -0.005 ? ` · PO ${fmtMoney(r.po_cover_ex)} is short of the contract` : ""}</div>
                  </div>
                  <div className={`text-right font-mono text-[13px] whitespace-nowrap ${over ? "text-danger" : "text-text"}`} title={over ? "Invoiced more than the recorded contract — a variation approved on site but not in Ops, or an old contract value" : "Still to claim"}>
                    {over ? `${fmtMoney(-stc)} over` : fmtMoney(stc)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// InvoiceDetailModal — T-237 + T-236
//
// Click-into view for any invoice (draft, sent, paid). Shows lines, totals,
// PDF status. Action buttons: upload Xero PDF (if missing), mark sent (if
// draft), mark paid (if sent). Replaces the in-memory-only post-generate UI
// so manager can resume any draft without losing work.
// =============================================================================
function InvoiceDetailModal({ invoiceId, onClose, onChanged }: {
  invoiceId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentRef, setPaymentRef] = useState("");
  // T-433 — edit lines after issue, so the ops invoice can be reshaped to
  // mirror exactly what went out in Xero (progress-claim %s, split claims).
  const [editingLines, setEditingLines] = useState(false);
  // source_quote_id rides along so a line added from a quote stays LINKED to it.
  // Without the link the quote still reads as unclaimed and the same variation
  // can be invoiced a second time - which is the failure this whole feature
  // exists to prevent, so it would be a poor way to build it.
  const [editRows, setEditRows] = useState<{
    line_id: string | null; description: string; amount: string;
    source_quote_id?: string | null; quote_number?: string;
  }[]>([]);

  // ---------------------------------------------------------------------------
  // T-511 — put the quote / variation picker on an ISSUED invoice.
  //
  // James, 21 Aug, on Nullarbor INV-26-00020: "We need a way in AR to go back to
  // the invoice where we select which quote / variation. Nullarbor missed a VAR
  // when we created it, fixed in Xero, re-upload and sync, it caught its delta -
  // but how do we go back to edit?"
  //
  // The delta the sync caught was $28,143.27, which is VAR-26-0004 exactly. Edit
  // lines could already fix the NUMBER by hand, but a hand-typed line is not
  // linked to the variation, so the variation stays unclaimed and can be
  // invoiced again later. The picker keeps the link.
  //
  // Why not simply reopen the worksheet: /projects/:id/invoice/new is a CREATE
  // screen. Its generate step inserts a new client_invoices row, claims a new
  // INV number and pushes a NEW invoice to Xero. Pointing an issued invoice at
  // it would produce a second invoice to the client, which is a worse problem
  // than the one being fixed.
  // ---------------------------------------------------------------------------
  const { data: inv, isLoading, refetch } = useQuery({
    queryKey: ["invoice_detail", invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_invoices")
        .select(`
          invoice_id, invoice_number, status, invoice_date, due_date,
          subtotal_excl_gst, gst_amount, total_incl_gst,
          sent_at, paid_at, pdf_file_id, xero_invoice_id, xero_invoice_number,
          payment_method, payment_reference,
          external_invoice_number, external_reference,
          docs:client_invoice_documents ( doc_id, doc_type, storage_path, file_name, doc_reference ),
          projects ( project_id, project_code, project_name, sites ( site_name ) ),
          clients  ( legal_name, accounts_contact_email, uses_rcti )
        `)
        .eq("invoice_id", invoiceId)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  const { data: lines = [] } = useQuery({
    queryKey: ["invoice_lines", invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_invoice_lines")
        .select("line_id, line_number, description, quantity, unit_price, line_total, source_quote_id")
        .eq("invoice_id", invoiceId)
        .order("line_number", { ascending: true });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: pdfFile } = useQuery({
    queryKey: ["invoice_pdf", inv?.pdf_file_id],
    enabled: !!inv?.pdf_file_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("files")
        .select("file_id, file_name, r2_bucket, r2_object_key")
        .eq("file_id", inv.pdf_file_id)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  // Upload PDF + mark sent
  const uploadPdfMut = useMutation({
    mutationFn: async (pdfFile: File) => {
      const objectKey = `client_invoices/${invoiceId}/${pdfFile.name}`;
      const signRes = await apiFetch("/sign-upload", {
        method: "POST",
        body: JSON.stringify({
          bucket:             "coretel-prod",
          object_key:         objectKey,
          content_type:       pdfFile.type || "application/pdf",
          expires_in_seconds: 900,
        }),
      });
      if (!signRes.ok) {
        const err = await signRes.text().catch(() => "");
        throw new Error(`sign-upload failed (${signRes.status}): ${err.slice(0, 200)}`);
      }
      const { upload_url } = await signRes.json();
      const putRes = await fetch(upload_url, {
        method:  "PUT",
        headers: { "Content-Type": pdfFile.type || "application/pdf" },
        body:    pdfFile,
      });
      if (!putRes.ok) throw new Error(`R2 PUT failed: ${putRes.status}`);

      const { data: fileRow, error: fErr } = await supabase
        .from("files")
        .insert({
          r2_bucket:       "coretel-prod",
          r2_object_key:   objectKey,
          file_name:       pdfFile.name,
          file_size_bytes: pdfFile.size,
          content_type:    pdfFile.type || "application/pdf",
          parent_table:    "client_invoices",
          parent_id:       invoiceId,
        })
        .select("file_id")
        .single();
      if (fErr) throw new Error(`Could not record file: ${fErr.message}`);

      const updates: any = { pdf_file_id: fileRow.file_id };
      if (inv.status === "draft") {
        updates.sent_at = new Date().toISOString();
        updates.status  = "sent";
      }
      const { error: uErr } = await supabase
        .from("client_invoices")
        .update(updates)
        .eq("invoice_id", invoiceId);
      if (uErr) throw new Error(`Could not update invoice: ${uErr.message}`);
    },
    onSuccess: () => { refetch(); onChanged(); },
  });

  // Mark sent without PDF (when manager already emailed via Outlook)
  const markSentMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("client_invoices")
        .update({ status: "sent", sent_at: new Date().toISOString() })
        .eq("invoice_id", invoiceId);
      if (error) throw error;
    },
    onSuccess: () => { refetch(); onChanged(); },
    onError: (e: any) => alert(`Couldn't mark the invoice sent: ${e?.message ?? e}`),   // T-403
  });

  // Mark paid (T-345). Works from draft OR sent; persists method + reference.
  // If the invoice was still a draft, stamp sent_at too so the lifecycle stays
  // coherent (draft → sent → paid in one action).
  const markPaidMut = useMutation({
    mutationFn: async () => {
      const patch: any = {
        status:            "paid",
        // 23 Oct 2026 — record the amount too. Mark-paid left amount_paid at 0,
        // which is why the Xero reconcile showed six "paid → paid · $0 → $X".
        amount_paid:       Number(inv.total_incl_gst ?? 0),
        paid_at:           paidDate ? new Date(paidDate + "T00:00:00").toISOString() : new Date().toISOString(),
        payment_method:    paymentMethod || null,
        payment_reference: paymentRef.trim() || null,
      };
      if (inv.status === "draft" && !inv.sent_at) patch.sent_at = new Date().toISOString();
      const { error } = await supabase
        .from("client_invoices")
        .update(patch)
        .eq("invoice_id", invoiceId);
      if (error) throw error;
    },
    onSuccess: () => { refetch(); onChanged(); },
    onError: (e: any) => window.alert(`Couldn't mark paid: ${e?.message ?? String(e)}`),
  });

  // T-504 — push this invoice to Xero. The Worker does every check itself and
  // hands back plain-English blockers; nothing is re-derived here, so the
  // screen and the Worker cannot drift apart and start disagreeing about
  // whether something is pushable.
  const [pushBlockers, setPushBlockers] = useState<string[]>([]);
  const pushMut = useMutation({
    mutationFn: async () => {
      setPushBlockers([]);
      const res = await apiFetch("/xero/ar/push", {
        method: "POST",
        body: JSON.stringify({ invoice_id: invoiceId }),
      });
      const j = await res.json().catch(() => ({} as any));
      if (res.status === 409 && j?.blockers) { setPushBlockers(j.blockers); throw new Error("blocked"); }
      if (!res.ok) throw new Error((j?.messages?.join(" | ") || j?.detail || j?.error) ?? `Push failed (${res.status})`);
      return j;
    },
    onSuccess: (j: any) => {
      refetch(); onChanged();
      qc.invalidateQueries({ queryKey: ["invoice_pdf"] });
      if (j?.pdf && j.pdf !== "ok") {
        window.alert(`${j.xero_number ?? "The invoice"} is in Xero, but its PDF didn't come back: ${j.pdf_detail ?? "unknown reason"}.\n\nYou can still download it from Xero and upload it here.`);
      }
    },
    onError: (e: any) => { if (e?.message !== "blocked") window.alert(`Couldn't push to Xero: ${e?.message ?? e}`); },
  });

  // T-504c — once an invoice is in Xero, Xero is where it gets edited. This
  // pulls the edit back rather than asking anyone to make it twice.
  const [refreshResult, setRefreshResult] = useState<any | null>(null);
  const refreshMut = useMutation({
    mutationFn: async () => {
      setRefreshResult(null);
      const res = await apiFetch("/xero/ar/refresh", {
        method: "POST",
        body: JSON.stringify({ invoice_id: invoiceId }),
      });
      const j = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error((j?.messages?.join(" | ") || j?.detail || j?.error) ?? `Refresh failed (${res.status})`);
      return j;
    },
    onSuccess: (j: any) => {
      setRefreshResult(j);
      refetch(); onChanged();
      qc.invalidateQueries({ queryKey: ["invoice_pdf"] });
    },
    onError: (e: any) => window.alert(`Couldn't refresh from Xero: ${e?.message ?? e}`),
  });

  // T-433 — save edited lines. Deletes removed rows, updates/creates the
  // rest with sequential numbering (two-pass to dodge any unique constraint
  // on (invoice_id, line_number)). The recompute_invoice_totals trigger then
  // refreshes subtotal/GST/total, and project financials cascade from there.
  // Same view the worksheet's picker reads. Only quotes with something left to
  // claim, and only while the invoice is still editable in this sense.
  const invProjectId: string | undefined = (inv as any)?.projects?.project_id;
  const { data: claimable = [] } = useQuery({
    queryKey: ["v_quote_invoiced_summary", invProjectId],
    enabled: !!invProjectId && editingLines,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_quote_invoiced_summary")
        .select("*")
        .eq("project_id", invProjectId!)
        .in("quote_status", ["accepted", "partially_invoiced", "invoiced"])
        .order("quote_type", { ascending: true })
        .order("quote_number", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{
        quote_id: string; quote_number: string; quote_title: string;
        quote_type: string; quote_total_ex_gst: number;
        invoiced_ex_gst: number; remaining_ex_gst: number; invoiced_pct: number;
      }>;
    },
  });

  // Anything already on this invoice - saved or just added - is not offered
  // again. Claiming the same variation twice on one document is not a thing
  // anybody means to do.
  const alreadyOn = new Set<string>([
    ...(lines as any[]).map(l => l.source_quote_id).filter(Boolean),
    ...editRows.map(r => r.source_quote_id).filter(Boolean) as string[],
  ]);
  const offerable = claimable.filter(
    q => q.remaining_ex_gst > 0.01 && !alreadyOn.has(q.quote_id));

  function addQuoteLine(q: typeof claimable[number]) {
    setEditRows(rs => [...rs, {
      line_id: null,
      description: defaultLineDescription(q.quote_number, q.quote_title, 100),
      amount: String(q.remaining_ex_gst.toFixed(2)),
      source_quote_id: q.quote_id,
      quote_number: q.quote_number,
    }]);
  }

  const saveLinesMut = useMutation({
    mutationFn: async () => {
      const rows = editRows
        .map(r => ({ ...r, amountNum: Number(String(r.amount).replace(/[^0-9.\-]/g, "")) }))
        .filter(r => r.description.trim() !== "" || r.line_id !== null);
      if (rows.length === 0) throw new Error("An invoice needs at least one line.");
      if (rows.some(r => !r.description.trim() || !Number.isFinite(r.amountNum))) {
        throw new Error("Every line needs a description and a valid ex-GST amount.");
      }
      const keepIds = new Set(rows.map(r => r.line_id).filter(Boolean));
      for (const l of lines as any[]) {
        if (keepIds.has(l.line_id)) continue;
        const { error } = await supabase.from("client_invoice_lines").delete().eq("line_id", l.line_id);
        if (error) throw error;
      }
      // Pass 1: park kept rows on temporary high line numbers.
      for (let i = 0; i < rows.length; i++) {
        if (!rows[i].line_id) continue;
        const { error } = await supabase.from("client_invoice_lines")
          .update({ line_number: 1000 + i }).eq("line_id", rows[i].line_id);
        if (error) throw error;
      }
      // Pass 2: final content + sequential numbering, inserting new rows.
      for (let i = 0; i < rows.length; i++) {
        const patch = {
          line_number: i + 1,
          description: rows[i].description.trim(),
          quantity:    1,
          unit_price:  rows[i].amountNum,
          line_total:  rows[i].amountNum,
        };
        if (rows[i].line_id) {
          const { error } = await supabase.from("client_invoice_lines").update(patch).eq("line_id", rows[i].line_id);
          if (error) throw error;
        } else {
          const { error } = await supabase.from("client_invoice_lines").insert({
            invoice_id: invoiceId,
            ...patch,
            // The link. A hand-typed line has none and that is fine - it is
            // mirroring something in Xero. A line picked from a quote keeps it.
            source_quote_id: rows[i].source_quote_id ?? null,
          });
          if (error) throw error;
        }
      }

      // ---- put every touched quote's status back where the numbers say ----
      //
      // Deliberately NOT "subtract what I just claimed" the way the worksheet's
      // generate step does. Generate only ever adds, so arithmetic is safe
      // there. Here a line can be added OR removed, and one-directional
      // arithmetic would leave a trap: add a variation, change your mind,
      // delete the line - and the quote stays 'invoiced' and drops off the
      // claimable list for good, recoverable only by hand in SQL.
      //
      // So instead: re-read the view AFTER the line writes and set each touched
      // quote's status from what is actually invoiced against it now. Correct in
      // both directions, and correct even if two people edit different invoices
      // against the same quote.
      const touched = new Set<string>();
      for (const r of rows) if (r.source_quote_id) touched.add(r.source_quote_id);
      for (const l of lines as any[]) {
        // Lines that were just deleted still need their quote reconsidered.
        if (!keepIds.has(l.line_id) && l.source_quote_id) touched.add(l.source_quote_id);
      }

      if (touched.size > 0) {
        const { data: fresh, error: vErr } = await supabase
          .from("v_quote_invoiced_summary")
          .select("quote_id, quote_number, quote_total_ex_gst, invoiced_ex_gst, remaining_ex_gst")
          .in("quote_id", Array.from(touched));
        if (vErr) {
          console.warn("Could not re-read quote claim status:", vErr);
        } else {
          for (const q of (fresh ?? []) as any[]) {
            const invoiced  = Number(q.invoiced_ex_gst ?? 0);
            const remaining = Number(q.remaining_ex_gst ?? 0);
            const status = remaining <= 0.01 ? "invoiced"
                         : invoiced  <= 0.01 ? "accepted"
                         : "partially_invoiced";
            const { error } = await supabase.from("quotes")
              .update({ status }).eq("quote_id", q.quote_id);
            if (error) console.warn(`Could not update quote ${q.quote_number} status:`, error);
          }
        }
      }
    },
    onSuccess: () => {
      setEditingLines(false);
      refetch();
      qc.invalidateQueries({ queryKey: ["invoice_lines", invoiceId] });
      qc.invalidateQueries({ queryKey: ["v_quote_invoiced_summary", invProjectId] });
      onChanged();
    },
    onError: (e: any) => window.alert(`Couldn't save the lines: ${e?.message ?? String(e)}`),
  });

  function startEditLines() {
    setEditRows((lines as any[]).map(l => ({
      line_id: l.line_id,
      description: l.description ?? "",
      amount: String(l.line_total ?? ""),
    })));
    setEditingLines(true);
  }

  // T-435 — unmark paid (director-only). Marked-paid-in-error happens: a
  // mis-tap, an allocation against the wrong invoice, or an RCTI that hadn't
  // actually cleared. Rolls status back to sent and CLEARS the payment
  // evidence so nothing stale is left implying money arrived. Deliberately
  // not offered to managers — this moves a revenue figure.
  const unmarkPaidMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("client_invoices")
        .update({
          status: "sent",
          paid_at: null,
          payment_method: null,
          payment_reference: null,
        })
        .eq("invoice_id", invoiceId);
      if (error) throw error;
    },
    onSuccess: () => { refetch(); onChanged(); },
    onError: (e: any) => window.alert(`Couldn't roll the invoice back: ${e?.message ?? String(e)}`),
  });

  // Sign the R2 object → return the signed GET URL (or null on failure).
  async function signInvoicePdf(): Promise<string | null> {
    if (!pdfFile) return null;
    const res = await apiFetch("/sign-download", {
      method: "POST",
      body: JSON.stringify({
        bucket:     pdfFile.r2_bucket,
        object_key: pdfFile.r2_object_key,
        expires_in_seconds: 300,
      }),
    });
    if (!res.ok) { window.alert(`Couldn't open the PDF (${res.status}).`); return null; }
    // Worker returns { download_url } (see /sign-download) — the old `url`
    // read meant every View/Download alerted "No file URL returned".
    const j = await res.json();
    const url = j.download_url ?? j.url;
    if (!url) { window.alert("No file URL returned — the PDF may not have finished uploading."); return null; }
    return url as string;
  }

  // View in a new tab. Reserve the tab synchronously (inside the click) so the
  // popup blocker doesn't orphan it as about:blank after the await, then point
  // it at the signed URL — the browser renders the PDF inline.
  async function viewPdf() {
    if (!pdfFile) return;
    const win = window.open("", "_blank", "noopener");
    const url = await signInvoicePdf();
    if (!url) { if (win) win.close(); return; }
    if (win) win.location.href = url; else window.open(url, "_blank", "noopener");
  }

  // Force a file save — fetch the bytes as a blob so we don't depend on the
  // cross-origin URL rendering.
  async function downloadPdf() {
    const url = await signInvoicePdf();
    if (!url) return;
    try {
      const fileRes = await fetch(url);
      if (!fileRes.ok) throw new Error(String(fileRes.status));
      const blob = await fileRes.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl; a.download = pdfFile?.file_name || `${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      // CORS or fetch failure — fall back to opening it in a tab.
      window.open(url, "_blank", "noopener");
    }
  }

  if (isLoading || !inv) return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-md p-8"><Spinner/></div>
    </div>
  );

  const statusColor =
    inv.status === "paid"  ? "bg-ok/10 text-ok border-ok/30" :
    inv.status === "sent"  ? "bg-accent/10 text-accent border-accent/30" :
    inv.status === "draft" ? "bg-warning/10 text-warning border-warning/30" :
    inv.status === "void"  ? "bg-text-dim/10 text-text-dim border-text-dim/30" :
                              "bg-ink-raised text-text-muted border-line";

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-md w-full max-w-[700px] my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[16px] text-accent">{inv.invoice_number}</span>
              <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded border ${statusColor}`}>{inv.status}</span>
            </div>
            <div className="text-[12px] text-text-muted mt-1">
              {inv.projects?.project_code}
              {inv.projects?.sites?.site_name ? ` · ${inv.projects.sites.site_name}` : ""}
              {" · "}{inv.clients?.legal_name}
            </div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Header KPIs */}
          <div className="grid grid-cols-3 gap-3 text-[12px]">
            <div>
              <div className="text-[10px] uppercase text-text-dim font-mono">Issued</div>
              <div className="font-mono text-text mt-0.5">{fmtDate(inv.invoice_date)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-text-dim font-mono">Due</div>
              <div className="font-mono text-text mt-0.5">{inv.due_date ? fmtDate(inv.due_date) : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-text-dim font-mono">Total inc GST</div>
              <div className="font-mono text-accent text-[14px] mt-0.5">{fmtMoney(inv.total_incl_gst)}</div>
            </div>
          </div>

          {/* Lines — T-433: editable after issue so ops mirrors Xero exactly */}
          <div className="border border-line rounded">
            <div className="px-3 py-2 bg-ink-raised text-[10px] uppercase tracking-wider2 text-text-muted font-mono border-b border-line flex items-center justify-between">
              <span>Lines ({lines.length})</span>
              {hasRole(user, "manager") && inv.status !== "void" && !editingLines && (
                <button type="button" onClick={startEditLines}
                  className="text-accent hover:underline normal-case tracking-normal text-[11px] font-sans">
                  Edit lines
                </button>
              )}
            </div>
            {editingLines ? (
              <div className="p-3 space-y-2">
                {inv.status === "paid" && (
                  <div className="bg-warning/10 border border-warning/30 rounded px-3 py-2 text-[11px] text-warning">
                    This invoice is marked paid — only edit it to mirror what actually went out in Xero.
                  </div>
                )}
                {/* The picker. Only while the invoice can still sensibly gain a
                    claim - a paid invoice gets the hand-edit path and its
                    existing warning, not a fresh claim against a quote. */}
                {(inv.status === "sent" || inv.status === "draft") && offerable.length > 0 && (
                  <div className="border border-line rounded-md">
                    <div className="px-2.5 py-1.5 bg-ink-raised border-b border-line text-[10px] uppercase tracking-wider2 text-text-muted font-mono">
                      Add a quote or variation
                    </div>
                    <div className="divide-y divide-line max-h-40 overflow-auto">
                      {offerable.map(q => (
                        <button key={q.quote_id} type="button" onClick={() => addQuoteLine(q)}
                          className="w-full text-left px-2.5 py-2 hover:bg-ink-hover flex items-center gap-2">
                          <Plus size={12} className="text-accent flex-shrink-0" />
                          <span className="min-w-0 flex-1">
                            <span className="text-[12px] text-text block truncate">
                              <span className="font-mono text-accent">{q.quote_number}</span>
                              {q.quote_type === "variation" && (
                                <span className="ml-1.5 text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-accent/12 text-accent">var</span>
                              )}
                              <span className="ml-1.5">{q.quote_title}</span>
                            </span>
                            <span className="text-[10px] text-text-muted">
                              {q.invoiced_ex_gst > 0
                                ? `${Math.round(q.invoiced_pct)}% claimed · ${fmtMoney(q.remaining_ex_gst)} left`
                                : `nothing claimed yet · ${fmtMoney(q.quote_total_ex_gst)}`}
                            </span>
                          </span>
                          <span className="font-mono text-[12px] text-text flex-shrink-0">
                            {fmtMoney(q.remaining_ex_gst)}
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="px-2.5 py-1.5 text-[10px] text-text-dim border-t border-line">
                      Adds the remaining amount as a new line and keeps it linked to the quote,
                      so it stops showing as claimable. Edit the figure after adding if you're
                      only claiming part of it.
                    </div>
                  </div>
                )}

                {editRows.map((r, i) => (
                  <div key={r.line_id ?? `new-${i}`} className="flex gap-2 items-center">
                    <Input value={r.description} placeholder="Line description (match the Xero line)"
                      onChange={e => setEditRows(rs => rs.map((x, xi) => xi === i ? { ...x, description: e.target.value } : x))}
                      className="flex-1"/>
                    <Input value={r.amount} placeholder="ex GST"
                      onChange={e => setEditRows(rs => rs.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))}
                      className="w-[120px] text-right font-mono"/>
                    <button type="button" onClick={() => setEditRows(rs => rs.filter((_, xi) => xi !== i))}
                      className="text-text-muted hover:text-danger p-1" aria-label="Remove line">
                      <Trash2 size={13}/>
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => setEditRows(rs => [...rs, { line_id: null, description: "", amount: "" }])}
                  className="text-[11px] text-accent hover:underline">+ Add line</button>
                {(() => {
                  const sub = editRows.reduce((s, r) => {
                    const n = Number(String(r.amount).replace(/[^0-9.\-]/g, ""));
                    return s + (Number.isFinite(n) ? n : 0);
                  }, 0);
                  return (
                    <div className="text-[11px] font-mono text-text-muted text-right space-y-0.5 pt-1 border-t border-line">
                      <div>Subtotal ex GST <span className="text-text ml-2">{fmtMoney(sub)}</span></div>
                      <div>GST <span className="ml-2">{fmtMoney(sub * 0.1)}</span></div>
                      <div>Total inc GST <span className="text-accent ml-2">{fmtMoney(sub * 1.1)}</span></div>
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2 pt-1">
                  <Button size="sm" onClick={() => saveLinesMut.mutate()} disabled={saveLinesMut.isPending}>
                    {saveLinesMut.isPending ? "Saving…" : "Save lines"}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setEditingLines(false)} disabled={saveLinesMut.isPending}>Cancel</Button>
                  <span className="text-[10px] text-text-dim">Invoice + project totals recompute automatically.</span>
                </div>
              </div>
            ) : (
            <table className="w-full text-[12px]">
              <tbody>
                {lines.map((l: any) => (
                  <tr key={l.line_id} className="border-b border-line last:border-0">
                    <td className="px-3 py-2 text-text-muted w-8 font-mono text-[10px]">{l.line_number}</td>
                    <td className="px-3 py-2 text-text">{l.description}</td>
                    <td className="px-3 py-2 font-mono text-right">{fmtMoney(l.line_total)}</td>
                  </tr>
                ))}
                <tr className="bg-ink-raised">
                  <td colSpan={2} className="px-3 py-2 text-right text-text-muted text-[11px] font-mono">Subtotal ex GST</td>
                  <td className="px-3 py-2 font-mono text-right">{fmtMoney(inv.subtotal_excl_gst)}</td>
                </tr>
                <tr>
                  <td colSpan={2} className="px-3 py-1 text-right text-text-muted text-[11px] font-mono">GST</td>
                  <td className="px-3 py-1 font-mono text-right text-text-muted">{fmtMoney(inv.gst_amount)}</td>
                </tr>
                <tr className="bg-ink-raised border-t border-line">
                  <td colSpan={2} className="px-3 py-2 text-right text-text font-mono">Total inc GST</td>
                  <td className="px-3 py-2 font-mono text-right text-accent">{fmtMoney(inv.total_incl_gst)}</td>
                </tr>
              </tbody>
            </table>
            )}
          </div>

          {/* T-504 — Xero */}
          <div className="border border-line rounded p-3">
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Xero</div>
            {inv.xero_invoice_id ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-[12px] text-ok">
                  <CheckCircle2 size={13}/>
                  In Xero as <span className="font-mono">{inv.xero_invoice_number ?? "an invoice"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <a href={xeroArInvoiceUrl(inv.xero_invoice_id)} target="_blank" rel="noopener noreferrer"
                     className="text-[12px] text-accent hover:underline inline-flex items-center gap-1">
                    Open in Xero <ExternalLink size={11}/>
                  </a>
                  {hasRole(user, "manager") && (
                    <button type="button" onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending}
                      className="text-[12px] text-text-muted hover:text-text underline disabled:opacity-50">
                      {refreshMut.isPending ? "Refreshing…" : "Refresh from Xero"}
                    </button>
                  )}
                </div>
              </div>
            ) : inv.status === "void" ? (
              <div className="text-[12px] text-text-muted">Void invoices aren't sent to Xero.</div>
            ) : (
              <>
                <div className="text-[12px] text-text-muted mb-2">
                  Creates the invoice in Xero, then brings its PDF back here. Xero assigns the
                  invoice number the client sees; {inv.invoice_number} rides along as the reference.
                </div>
                {hasRole(user, "manager") && (
                  <Button icon={Send} onClick={() => pushMut.mutate()} disabled={pushMut.isPending}>
                    {pushMut.isPending ? "Pushing…" : "Push to Xero"}
                  </Button>
                )}
                {pushBlockers.length > 0 && (
                  <div className="mt-2 bg-warning/10 border border-warning/30 rounded px-3 py-2 text-[12px] text-warning">
                    <div className="font-medium mb-1">Not ready to push:</div>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {pushBlockers.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>

          {refreshResult && (
            <div className="border border-line rounded p-3 text-[12px] space-y-2">
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">Last refresh from Xero</div>
              {refreshResult.changed === 0 ? (
                <div className="text-text-muted">Coretel already matched Xero. Nothing to change.</div>
              ) : (
                <ul className="space-y-0.5">
                  {(refreshResult.changes ?? []).map((ch: any, i: number) => (
                    <li key={i} className="text-text-muted">
                      <span className="text-text">{String(ch.field).replace(/_/g, " ")}</span>{" "}
                      <span className="text-text-dim">{String(ch.from ?? "—")}</span> → <span className="text-ok">{String(ch.to)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {refreshResult.pdf === "ok" && <div className="text-text-muted">PDF re-pulled from Xero.</div>}
              {refreshResult.amount_variance && (
                <div className="bg-warning/10 border border-warning/30 rounded px-3 py-2 text-warning">
                  Xero has this invoice at {fmtMoney(refreshResult.amount_variance.xero_total)}, Coretel has{" "}
                  {fmtMoney(refreshResult.amount_variance.ops_total)} — a difference of{" "}
                  {fmtMoney(Math.abs(refreshResult.amount_variance.difference))}. The total here is worked out
                  from the lines, so it hasn't been overwritten. Edit the lines to match, or leave it if the
                  difference is deliberate.
                </div>
              )}
              {refreshResult.skipped_payment_has_allocations && (
                <div className="text-[11px] text-text-dim">
                  Payment left alone — a receipt has been allocated to this invoice here, and that figure
                  is owned by the allocation.
                </div>
              )}
            </div>
          )}

          {/* PDF section */}
          <div className="border border-line rounded p-3">
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Xero PDF</div>
            {inv.pdf_file_id && pdfFile ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-[12px] text-text font-mono truncate">{pdfFile.file_name}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button variant="secondary" size="sm" icon={ExternalLink} onClick={viewPdf}>View</Button>
                  <Button variant="secondary" size="sm" onClick={downloadPdf}>Download</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-[12px] text-text-muted mb-2">
                  {inv.xero_invoice_id
                    ? "No PDF attached — the pull-back from Xero didn't land. Download it from Xero and upload it here."
                    : "No PDF attached. Push to Xero above, or upload the file Xero generated."}
                </div>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadPdfMut.mutate(f); }}
                  disabled={uploadPdfMut.isPending}
                  className="block w-full text-[12px] text-text file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-line file:bg-ink-raised file:text-text hover:file:bg-ink-card"
                />
                {uploadPdfMut.isError && (
                  <div className="mt-2 bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">
                    {(uploadPdfMut.error as any)?.message}
                  </div>
                )}
                {uploadPdfMut.isPending && <div className="mt-2 text-[12px] text-text-muted"><Spinner/> Uploading…</div>}
              </>
            )}
          </div>

          {/* Mark paid — available for draft or sent (T-345). A draft gets
              stamped sent + paid in one go. */}
          {(inv.status === "sent" || inv.status === "draft" || inv.status === "part_paid") && (
            <div className="border border-line rounded p-3">
              <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Mark paid</div>
              {inv.xero_invoice_id && (
                <div className="text-[11px] text-text-muted mb-2">
                  This one is in Xero, so <span className="text-text">Reconcile against Xero</span> on the Invoices page will set paid / part paid from the bank feed. Mark it paid here only if Xero can't. A part payment is <span className="text-text">Record payment</span>, not this.
                </div>
              )}
              {inv.status === "draft" && (
                <div className="text-[11px] text-text-muted mb-2">
                  This invoice is still a draft — marking it paid will also mark it sent.
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 mb-2">
                <Field label="Paid date">
                  <Input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
                </Field>
                <Field label="Method">
                  <Select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                    <option value="bank_transfer">Bank transfer</option>
                    <option value="eft">EFT</option>
                    <option value="cheque">Cheque</option>
                    <option value="other">Other</option>
                  </Select>
                </Field>
                <Field label="Reference (optional)">
                  <Input value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="EFT ref" />
                </Field>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <Button icon={CheckCircle2} onClick={() => markPaidMut.mutate()} disabled={markPaidMut.isPending}>
                  {markPaidMut.isPending ? "Marking…" : "Mark paid"}
                </Button>
              </div>
            </div>
          )}

          {/* T-410 — RCTI mirror card: the client issued THIS invoice (TSL doc);
              record their number + WCR/STOW refs and attach the evidence. */}
          {(inv.clients?.uses_rcti || inv.external_invoice_number || (inv.docs ?? []).length > 0) && (
            <RctiCard inv={inv} onChanged={() => { refetch(); onChanged(); }} />
          )}

          {/* Paid summary — show how it was settled once paid. */}
          {inv.status === "paid" && (
            <div className="border border-ok/30 bg-ok/5 rounded p-3 text-[12px]">
              <div className="flex items-center gap-2 text-ok">
                <CheckCircle2 size={13} /> Paid{inv.paid_at ? ` ${fmtDate(inv.paid_at)}` : ""}
              </div>
              {(inv.payment_method || inv.payment_reference) && (
                <div className="text-text-muted mt-1">
                  {inv.payment_method ? inv.payment_method.replace(/_/g, " ") : ""}
                  {inv.payment_method && inv.payment_reference ? " · " : ""}
                  {inv.payment_reference ? `ref ${inv.payment_reference}` : ""}
                </div>
              )}
              {hasRole(user, "director") && (
                <button
                  type="button"
                  disabled={unmarkPaidMut.isPending}
                  onClick={() => {
                    if (!window.confirm("Roll this invoice back to SENT?\n\nThe paid date, method and reference will be cleared. Only do this if it was marked paid in error — check the payment isn't sitting in Xero or the bank first.")) return;
                    unmarkPaidMut.mutate();
                  }}
                  className="text-[11px] text-text-muted hover:text-danger underline mt-2"
                >
                  {unmarkPaidMut.isPending ? "Rolling back…" : "Marked paid in error? Roll back to sent"}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-line bg-ink-raised flex items-center justify-end gap-2">
          {inv.status === "draft" && !inv.pdf_file_id && (
            <Button variant="secondary" onClick={() => markSentMut.mutate()} disabled={markSentMut.isPending}>
              Mark sent (no PDF)
            </Button>
          )}
          {/* 23 Oct 2026 — James: one way to the project's invoices from here,
              not links scattered over the row. The worksheet shows past claims
              and what's left to claim. */}
          {inv.projects?.project_id && (
            <Link to={`/projects/${inv.projects.project_id}/invoice/new`}>
              <Button variant="secondary" icon={DollarSign}>Project invoices →</Button>
            </Link>
          )}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// RctiCard — T-410. For RCTI clients (Ventia, Visionstream) the CLIENT issues
// the tax invoice (TSL doc) against our claim. This card records their
// identifiers on our mirror row and holds the evidence:
//   external_invoice_number = their RCTI/TSL number (e.g. 5115797336)
//   external_reference      = WCR id / NBNFRCTI no / STOW number
//   docs                    = their RCTI PDF + the remittance advice
// -----------------------------------------------------------------------------
function RctiCard({ inv, onChanged }: { inv: any; onChanged: () => void }) {
  const [extNo, setExtNo]   = useState<string>(inv.external_invoice_number ?? "");
  const [extRef, setExtRef] = useState<string>(inv.external_reference ?? "");
  const [docType, setDocType] = useState("rcti_invoice");
  const [docRef, setDocRef]   = useState("");   // T-410 — per-doc TSL number (multi-RCTI)
  const [busy, setBusy] = useState<string | null>(null);
  const dirty = extNo !== (inv.external_invoice_number ?? "") || extRef !== (inv.external_reference ?? "");

  const saveMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("client_invoices").update({
        external_invoice_number: extNo.trim() || null,
        external_reference: extRef.trim() || null,
      }).eq("invoice_id", inv.invoice_id);
      if (error) throw error;
    },
    onSuccess: onChanged,
    onError: (e: any) => alert(`Save failed: ${e?.message ?? e}`),
  });

  async function viewDoc(path: string) {
    const { data, error } = await supabase.storage.from("ar-documents").createSignedUrl(path, 3600);
    if (error || !data?.signedUrl) { alert(error?.message ?? "Couldn't open document"); return; }
    window.open(data.signedUrl, "_blank", "noopener");
  }

  async function uploadDoc(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setBusy("upload");
    try {
      const safe = file.name.replace(/[^\w.\-]+/g, "_");
      const path = `invoices/${inv.invoice_id}/${Date.now()}-${safe}`;
      const { error: upErr } = await supabase.storage.from("ar-documents")
        .upload(path, file, { contentType: file.type || "application/pdf", upsert: false });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from("client_invoice_documents")
        .insert({
          invoice_id: inv.invoice_id, doc_type: docType, storage_path: path,
          file_name: file.name, doc_reference: docRef.trim() || null,
        });
      if (insErr) throw insErr;
      setDocRef("");
      onChanged();
    } catch (e: any) {
      alert(`Upload failed: ${e?.message ?? e}`);
    } finally {
      setBusy(null);
    }
  }

  const DOC_LABEL: Record<string, string> = {
    rcti_invoice: "RCTI invoice", remittance: "Remittance", claim: "Claim", other: "Doc",
  };

  return (
    <div className="border border-accent/25 bg-accent/5 rounded p-3 space-y-2.5">
      <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-semibold">
        Client-issued invoice (RCTI) — their numbers, our mirror
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Their invoice # (TSL)">
          <Input value={extNo} onChange={e => setExtNo(e.target.value)} placeholder="e.g. 5115797336" />
        </Field>
        <Field label="WCR / STOW refs">
          <Input value={extRef} onChange={e => setExtRef(e.target.value)} placeholder="e.g. WCR 235531 · STOW 54843605" />
        </Field>
      </div>
      {dirty && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save refs"}
          </Button>
        </div>
      )}
      {/* Evidence docs */}
      <div className="flex items-center gap-2 flex-wrap">
        {(inv.docs ?? []).map((d: any) => (
          <button key={d.doc_id} onClick={() => viewDoc(d.storage_path)}
            className="inline-flex items-center gap-1 text-[11.5px] px-2 py-1 rounded border border-line hover:border-accent text-text-muted hover:text-text"
            title={d.file_name}>
            <FileText size={11} /> {DOC_LABEL[d.doc_type] ?? d.doc_type}
            {d.doc_reference ? ` ${d.doc_reference}` : `: ${d.file_name}`}
          </button>
        ))}
        <select value={docType} onChange={e => setDocType(e.target.value)}
          className="bg-ink border border-line rounded px-1.5 py-1 text-[11px] text-text">
          <option value="rcti_invoice">RCTI invoice</option>
          <option value="remittance">Remittance</option>
          <option value="claim">Claim</option>
          <option value="other">Other</option>
        </select>
        <input
          value={docRef}
          onChange={e => setDocRef(e.target.value)}
          placeholder="ref # (e.g. 5115797337)"
          className="bg-ink border border-line rounded px-1.5 py-1 text-[11px] text-text w-36 placeholder:text-text-dim"
          title="Optional — the TSL/doc number this file is (lets one invoice carry several RCTIs)"
        />
        <label className={`inline-flex items-center gap-1 text-[11.5px] px-2 py-1 rounded border border-dashed border-line cursor-pointer text-text-muted hover:border-accent hover:text-text ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          {busy ? "Uploading…" : "+ Attach"}
          <input type="file" className="hidden" onChange={uploadDoc} />
        </label>
      </div>
      <div className="text-[10.5px] text-text-dim italic">
        Their invoice # is the bank-rec key; the remittance doc # goes on the payment when you record it.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// XeroReconcileModal — T-504
//
// Xero holds the bank feed, so Xero is the truth about what has been paid.
// Ops was being updated by hand afterwards and drifted: on 13 Aug it was
// carrying a fully paid HVAC invoice as the largest overdue debt in the
// business, which fed the Finance hub, the Director hub, paid_to_date on the
// project and therefore that project's margin.
//
// Deliberately two steps. The first run is a DRY RUN that changes nothing and
// shows exactly what it would do; you press Apply only once you have looked
// at it. Nothing about "Xero is the source of truth" means a script should
// rewrite twenty invoices without anyone seeing the list first.
// -----------------------------------------------------------------------------
function XeroReconcileModal({ onClose, onApplied }: { onClose: () => void; onApplied: () => void }) {
  const [result, setResult] = useState<any | null>(null);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [applied, setApplied] = useState(false);
  const [backfill, setBackfill] = useState(true);

  async function run(dryRun: boolean) {
    setBusy(true); setError(null);
    try {
      const res = await apiFetch("/xero/ar/sync", {
        method: "POST",
        body: JSON.stringify({ dry_run: dryRun, backfill }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.detail ?? j?.error ?? `Sync failed (${res.status})`);
      setResult(j);
      if (!dryRun) { setApplied(true); onApplied(); }
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { run(true); /* eslint-disable-next-line */ }, []);

  const updates    = (result?.updated ?? []) as any[];
  const candidates = (result?.backfill_candidates ?? []) as any[];
  const skipped    = (result?.skipped_has_allocations ?? []) as any[];
  const unmatched  = (result?.unmatched_in_xero ?? []) as any[];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-md w-full max-w-[760px] my-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <div className="text-[15px] font-semibold text-text">Reconcile against Xero</div>
            <div className="text-[12px] text-text-muted mt-0.5">
              Xero holds the bank feed, so its view of what has been paid wins.
            </div>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text" type="button">×</button>
        </div>

        <div className="p-5 space-y-4">
          {busy && <div className="py-8 text-center text-[12px] text-text-muted"><Spinner/> Reading Xero…</div>}

          {error && (
            <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{error}</div>
          )}

          {result && !busy && (
            <>
              <div className="text-[12px] text-text-muted">
                Read {result.xero_invoices_read} client invoices from Xero.
                {applied ? " Changes applied." : " Nothing has been changed yet."}
              </div>

              {updates.length === 0 ? (
                <div className="border border-line rounded px-3 py-4 text-[12px] text-text-muted italic text-center">
                  Ops already agrees with Xero on every invoice it can identify.
                </div>
              ) : (
                <div className="border border-line rounded">
                  <div className="px-3 py-2 bg-ink-raised text-[10px] uppercase tracking-wider2 text-text-muted font-mono border-b border-line">
                    {applied ? "Changed" : "Would change"} ({updates.length})
                  </div>
                  <div className="max-h-[280px] overflow-y-auto divide-y divide-line">
                    {updates.map((u: any) => (
                      <div key={u.invoice_id} className="px-3 py-2 text-[12px] flex items-center justify-between gap-3 flex-wrap">
                        <div className="font-mono text-text">
                          {u.invoice_number}
                          {u.xero_number ? <span className="text-text-dim"> · Xero {u.xero_number}</span> : null}
                        </div>
                        <div className="text-text-muted">
                          <span className="text-text-dim">{u.from.status}</span>
                          {" → "}
                          <span className="text-ok">{u.to.status}</span>
                          {" · paid "}
                          <span className="text-text-dim">{fmtMoney(u.from.amount_paid)}</span>
                          {" → "}
                          <span className="text-text">{fmtMoney(u.to.amount_paid)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {candidates.length > 0 && (
                <div className="border border-warning/30 bg-warning/5 rounded p-3 text-[12px]">
                  <div className="text-warning font-medium mb-1">
                    Matched by invoice number only ({candidates.length})
                  </div>
                  <div className="text-text-muted mb-2">
                    These were raised in Xero by hand, so ops has no Xero id for them. Ticking the box
                    below records the id where the totals agree, which is what lets them stay in sync
                    from now on.
                  </div>
                  <div className="space-y-1">
                    {candidates.map((x: any) => (
                      <div key={x.invoice_id} className="flex items-center justify-between gap-3 flex-wrap">
                        <span className="font-mono text-text">{x.invoice_number} · Xero {x.xero_number}</span>
                        <span className={x.totals_agree ? "text-ok" : "text-danger"}>
                          {x.totals_agree
                            ? `${fmtMoney(x.ops_total)} · totals agree`
                            : `ops ${fmtMoney(x.ops_total)} vs Xero ${fmtMoney(x.xero_total)} — left alone`}
                        </span>
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 mt-2 text-text">
                    <input type="checkbox" checked={backfill} onChange={e => setBackfill(e.target.checked)} />
                    Record the Xero id where the totals agree
                  </label>
                </div>
              )}

              {skipped.length > 0 && (
                <div className="text-[11px] text-text-dim">
                  {skipped.length} invoice{skipped.length === 1 ? "" : "s"} left alone because a payment was
                  allocated to {skipped.length === 1 ? "it" : "them"} here — that figure is owned by the
                  receipt allocation, not by this sync.
                </div>
              )}

              {unmatched.length > 0 && (
                <div className="text-[11px] text-text-dim">
                  {unmatched.length} ops invoice{unmatched.length === 1 ? "" : "s"} had no match in Xero at all
                  ({unmatched.slice(0, 6).map((u: any) => u.invoice_number).join(", ")}
                  {unmatched.length > 6 ? "…" : ""}). Either never raised in Xero, or raised under a
                  different number.
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-line bg-ink-raised flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>{applied ? "Done" : "Cancel"}</Button>
          {!applied && result && (updates.length > 0 || candidates.length > 0) && (
            <Button icon={CheckCircle2} onClick={() => run(false)} disabled={busy}>
              {busy ? "Applying…" : "Apply changes"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
